/**
 * DeliveryPOSPage — Liefersoft-style two-step delivery order flow.
 *
 * Step 1 ("customer"): phone-first entry with live customer autocomplete from
 * `customers` collection, address form, OpenStreetMap preview, recent orders list.
 *
 * Step 2 ("cart"): three-column layout — cart on the left, image-grid product
 * picker in the middle, category pills on the right. Modals for Preorder,
 * Delivery time, Extras, Coupon. Saves the order to Firestore on Print.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Button, TextField, IconButton, Checkbox, Dialog,
  DialogContent, Snackbar, Alert, InputAdornment, CircularProgress, Drawer,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Search as SearchIcon, Close as CloseIcon,
  Add as AddIcon, Remove as RemoveIcon, ArrowBack as BackIcon,
  Print as PrintIcon, Schedule as ScheduleIcon, Redeem as CouponIcon,
  LocalOffer as ExtrasIcon, DeliveryDining as DeliveryIcon,
  ViewModule as GridIcon, ViewList as ListIcon,

  ReceiptLong as AllOrdersIcon, PointOfSale as DrawerIcon,
  MoneyOff as WithdrawIcon, Store as StoreIcon,
  ShoppingBag as PickupIcon, TableRestaurant as TableIcon,
  ExpandLess as ChevronUpIcon, ShoppingCart as CartIcon,
} from '@mui/icons-material';
import {
  collection, getDocs, addDoc, query, where, onSnapshot,
  serverTimestamp, getFirestore, doc, updateDoc, setDoc,
} from '../../services/firestoreCompat';
import { MapContainer, TileLayer, CircleMarker, Tooltip as LeafletTooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { MenuItem, Category } from '../../services/firebaseMenuService';
import FirestorePrintService from '../../services/firestorePrintService';
import FirebaseOrderService from '../../services/firebaseOrderService';
import { currentBranchId, getHiddenCategories } from '../../config/branchContext';
import AddItemWithAddOnsModal from '../../components/AddItemWithAddOnsModal';
import MenuImage from '../../components/MenuImage';

// Single static libraries array so the script tag doesn't re-load every render.
// (MAP_LIBRARIES moved to shared googleMapsLoader.ts — unused here now)

// ── Types ──────────────────────────────────────────────────────────
interface CustomerSuggestion {
  id: string;
  phone: string;
  name: string;
  firstName?: string;
  lastName?: string;
  address: string;
  postcode: string;
  city: string;
  customerNumber?: string;
  company?: string;        // company name if this customer ordered as a company
  isCompany?: boolean;
}
interface CartLine {
  id: string;          // unique line id (uuid)
  itemId: string;      // menu_items doc id, or 'extra-...' for ad-hoc
  name: string;
  qty: number;
  unitPrice: number;
  notes?: string;
  isExtra?: boolean;
  addons?: Array<{ name: string; price: number; quantity: number }>;
}
interface CustomerForm {
  phone: string;
  name: string;
  company: string;       // company name when isCompany=true
  street: string;
  houseNumber: string;
  customerNumber: string;
  comment: string;
  deliveryFee: number;
  isCompany: boolean;
  keepFormOnScreen: boolean;
}

// "Keep form on screen" defaults to OFF (Liefersoft default) — each new order
// starts with a clean form so the previous customer's details never leak into
// the next call by accident. Cashier can flip it on for batch-entry scenarios.
const EMPTY_CUSTOMER: CustomerForm = {
  phone: '', name: '', company: '', street: '', houseNumber: '',
  customerNumber: '', comment: '', deliveryFee: 0,
  isCompany: false, keepFormOnScreen: false,
};

// Surface colors so styling reads cleanly throughout the file.
const C = {
  surface: '#252838',
  surfaceAlt: '#1e2139',
  border: '#3a3d52',
  borderSubtle: '#2a2d40',
  accent: '#94e815',
  accentDark: '#7bc912',
  text: '#FFFFFF',
  textMuted: '#cbd5e1',
  textDim: '#94a3b8',
  danger: '#ef4444',
};

// Compact field styling. Labels need to stay readable while floating on top
// of the map (no panel background behind them), so we paint them in white and
// add a strong text-shadow so they show up over any map colour.
const inputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: `${C.surfaceAlt} !important`,
    borderRadius: '12px',
    color: `${C.text} !important`,
    minHeight: 44,
    '& fieldset': { borderColor: `${C.border} !important`, borderWidth: '1.5px' },
    '&:hover fieldset': { borderColor: `${C.accent} !important` },
    '&.Mui-focused fieldset': { borderColor: `${C.accent} !important` },
  },
  '& .MuiOutlinedInput-input': {
    color: `${C.text} !important`,
    paddingTop: '10px !important',
    paddingBottom: '10px !important',
    fontSize: '0.95rem',
  },
  '& .MuiInputLabel-root': {
    color: `#FFFFFF !important`,
    fontSize: '0.72rem',
    fontWeight: 800,
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    textShadow: '0 0 6px rgba(0,0,0,0.85), 0 1px 2px rgba(0,0,0,0.9)',
    backgroundColor: 'rgba(26,29,46,0.85)',
    px: 0.6,
    borderRadius: '4px',
  },
  '& .MuiInputLabel-root.Mui-focused': { color: `${C.accent} !important` },
};

// Module-level cart cache — lives outside component lifecycle so cart survives
// any navigation (unmount/remount, React Router route reuse, mobile app switching).
// localStorage is the persistent backup across full page reloads.
const _cartCache: { cashdesk: CartLine[]; delivery: CartLine[] } = { cashdesk: [], delivery: [] };

// ── Component ──────────────────────────────────────────────────────
const DeliveryPOSPage: React.FC = () => {
  const { language } = useLanguage();
  const isDe = false; // Saheeh: en/ar only — UI always shows English strings
  void language;
  const navigate = useNavigate();

  // Step flow + order type. orderType lets Pickup / Counter buttons enter the
  // cart without requiring a delivery address.
  // Cash desk mode (route /cashdesk) reuses this page but skips the
  // customer-details form — there's no delivery address to capture, so the
  // cashier lands directly on the product grid in Counter mode.
  const location = useLocation();
  const isCashDesk = location.pathname.startsWith('/cashdesk');
  const [step, setStep] = useState<'customer' | 'cart'>(isCashDesk ? 'cart' : 'customer');
  const [orderType, setOrderType] = useState<'delivery' | 'pickup' | 'counter'>(isCashDesk ? 'counter' : 'delivery');

  // React Router reuses this component across /delivery and /cashdesk, so
  // useState initialisers only fire on the first mount. Without this sync,
  // navigating Delivery → Cash desk leaves `step` stuck on 'customer' and the
  // cashier sees the delivery address form on /cashdesk.
  useEffect(() => {
    if (isCashDesk) {
      setStep('cart');
      if (orderType === 'delivery') setOrderType('counter');
    } else {
      setStep('customer');
      setOrderType('delivery');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCashDesk]);

  // Customer form
  const [customer, setCustomer] = useState<CustomerForm>(() => {
    try {
      const saved = localStorage.getItem('pos_keep_form_on_screen');
      return { ...EMPTY_CUSTOMER, keepFormOnScreen: saved === '1' };
    } catch {
      return EMPTY_CUSTOMER;
    }
  });

  // "Required" UI flag — Phone and Name share a soft OR rule (at least one
  // must be filled). When BOTH are empty we paint an orange "Required" badge
  // and orange field border on both, matching Liefersoft's UX. The moment
  // the cashier types in either, both highlights vanish.
  const needsAnyCustomerField = !customer.phone.trim() && !customer.name.trim();

  // Translucency state for the floating form panel. While `keepFormOnScreen`
  // is OFF, the panel fades when the cashier's pointer/focus leaves it so the
  // map stays visible. Hovering or focusing inside flips this back to true.
  const [formActive, setFormActive] = useState(false);
  const [suggestions, setSuggestions] = useState<CustomerSuggestion[]>([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [allCustomers, setAllCustomers] = useState<CustomerSuggestion[]>([]);

  // Next sequential customer number to assign to a brand-new customer. We use
  // (max existing numeric customerNumber) + 1, falling back to 1001.
  const nextCustomerNumber = useMemo(() => {
    let max = 1000;
    for (const c of allCustomers) {
      const n = parseInt(String(c.customerNumber || '').replace(/[^\d]/g, ''), 10);
      if (!isNaN(n) && n > max) max = n;
    }
    return String(max + 1);
  }, [allCustomers]);

  // Menu data
  const [items, setItems] = useState<MenuItem[]>([]);
  const [cats, setCats] = useState<Category[]>([]);
  const [loadingMenu, setLoadingMenu] = useState(true);
  const [productSearch, setProductSearch] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  // Multi-category selection — empty set means "All"
  const [activeCats, setActiveCats] = useState<Set<string>>(new Set());
  const toggleCat = (name: string) => setActiveCats(prev => {
    const next = new Set(prev);
    if (next.has(name)) { next.delete(name); } else { next.add(name); }
    return next;
  });

  // Cart — dual persistence: module-level cache (survives navigation within JS session)
  // + localStorage (survives page reload / app kill on mobile).
  const cartCacheKey = isCashDesk ? 'cashdesk' : 'delivery';
  const cartStorageKey = isCashDesk ? 'saheeh_pos_cashdesk_cart' : 'saheeh_pos_delivery_cart';
  const [cart, setCart] = useState<CartLine[]>(() => {
    // 1. Check in-memory module cache first (fastest, no JSON parse)
    if (_cartCache[cartCacheKey].length > 0) return _cartCache[cartCacheKey];
    // 2. Fall back to localStorage for cross-reload persistence
    try {
      const saved = localStorage.getItem(cartStorageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          _cartCache[cartCacheKey] = parsed; // warm the in-memory cache
          return parsed;
        }
      }
    } catch {}
    return [];
  });
  const [discountPct, setDiscountPct] = useState<number>(0);
  const [discountAutoApplied, setDiscountAutoApplied] = useState(false);

  // Track previous cache key to detect route switch via component reuse
  // (React Router reuses this component between /cashdesk and /delivery)
  const prevCartCacheKeyRef = useRef<string>(cartCacheKey);

  // Keep module cache + localStorage in sync on every cart change.
  // CRITICAL: when cartCacheKey changes (delivery ↔ cashdesk route switch),
  // we must save the current cart under the OLD key and RESTORE the new route's
  // cart — otherwise the empty delivery cart would overwrite the cashdesk cache.
  useEffect(() => {
    if (prevCartCacheKeyRef.current !== cartCacheKey) {
      // Route switched — save current cart to the OLD key first
      const oldKey = prevCartCacheKeyRef.current as 'cashdesk' | 'delivery';
      const oldStorageKey = oldKey === 'cashdesk' ? 'saheeh_pos_cashdesk_cart' : 'saheeh_pos_delivery_cart';
      _cartCache[oldKey] = cart;
      try {
        if (cart.length > 0) localStorage.setItem(oldStorageKey, JSON.stringify(cart));
        else localStorage.removeItem(oldStorageKey);
      } catch {}
      prevCartCacheKeyRef.current = cartCacheKey;

      // Restore the new route's cart from cache or localStorage
      let newCart: CartLine[] = _cartCache[cartCacheKey];
      if (newCart.length === 0) {
        try {
          const saved = localStorage.getItem(cartStorageKey);
          if (saved) {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed) && parsed.length > 0) newCart = parsed;
          }
        } catch {}
      }
      setCart(newCart);
      return; // will sync to new key on the next render cycle
    }
    // Normal: save current cart to current route's cache + localStorage
    _cartCache[cartCacheKey] = cart;
    try {
      if (cart.length > 0) localStorage.setItem(cartStorageKey, JSON.stringify(cart));
      else localStorage.removeItem(cartStorageKey);
    } catch {}
  }, [cart, cartCacheKey, cartStorageKey]);

  // Read the saved per-channel/per-weekday discount rules and auto-apply
  // the matching one. The cashier can still type over it (we only override
  // while we know the user hasn't manually changed it).
  useEffect(() => {
    let unsub: (() => void) | undefined;
    (async () => {
      try {
        const { onSnapshot, doc, getFirestore } = await import('../../services/firestoreCompat');
        const db = getFirestore();
        unsub = onSnapshot(doc(db, 'settings', 'discount_rules'), (snap) => {
          const data = snap.data() as any;
          const rules = data?.kasse;
          if (!rules) return;
          // JS Sunday=0..Saturday=6 → Mo..So index 0..6
          const jsDay = new Date().getDay();
          const moIdx = (jsDay + 6) % 7;
          const channelRule = rules[orderType];
          if (!channelRule) return;
          const applies = Array.isArray(channelRule.days) ? channelRule.days[moIdx] : true;
          const pct = Number(channelRule.percent) || 0;
          if (applies && pct > 0) {
            // Only auto-apply if cashier hasn't typed something different
            setDiscountPct(curr => (curr === 0 || discountAutoApplied) ? pct : curr);
            setDiscountAutoApplied(true);
          }
        });
      } catch { /* non-critical */ }
    })();
    return () => { if (unsub) unsub(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType]);

  // Modals
  const [preorderOpen, setPreorderOpen] = useState(false);
  const [preorderDate, setPreorderDate] = useState<Date | null>(null);
  const [deliveryTimeOpen, setDeliveryTimeOpen] = useState(false);
  const [deliveryMinutes, setDeliveryMinutes] = useState<number | null>(null);
  const [extrasOpen, setExtrasOpen] = useState(false);
  const [extrasForm, setExtrasForm] = useState({ description: '', price: '', type: 'Article' as 'Article'|'Beverage'|'Deposit'|'Tobacco' });
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponCode, setCouponCode] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({ open: false, msg: '', sev: 'success' });

  // ── Load customers + menu on mount ──────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const db = getFirestore();
        const snap = await getDocs(collection(db, 'customers'));
        const list: CustomerSuggestion[] = [];
        snap.forEach(d => {
          const data = d.data() as any;
          if (!data.phone && !data.name) return;
          list.push({
            id: d.id,
            phone: String(data.phone || '').replace(/\s/g, ''),
            name: data.name || `${data.firstName || ''} ${data.lastName || ''}`.trim(),
            firstName: data.firstName,
            lastName: data.lastName,
            address: data.address || '',
            postcode: data.postcode || '',
            city: data.city || '',
            customerNumber: data.customerNumber || data.customer_number || '',
            company: data.company || data.company_name || '',
            isCompany: !!(data.is_company || data.isCompany || data.company),
          });
        });
        setAllCustomers(list);
      } catch (e) { console.warn('customers load', e); }
    })();
  }, []);

  // Live menu + category sync — onSnapshot keeps POS in lock-step with admin
  // changes without a page reload. Categories are filtered to only those that
  // have at least one visible item, eliminating orphan/duplicate-brand entries.
  useEffect(() => {
    const db = getFirestore();
    const hiddenCats = getHiddenCategories();
    const isHidden = (catName: string) => hiddenCats.includes((catName || '').toLowerCase().trim());

    let latestItems: MenuItem[] = [];
    let latestCats: Category[] = [];

    const applyFilters = () => {
      const visibleItems = latestItems.filter(
        m => m.is_available !== false && !m.sold_out && !isHidden(m.category || '')
      );
      // Build set of category names that have at least one visible item
      const itemCatNames = new Set(visibleItems.map(m => (m.category || '').toLowerCase().trim()));
      const visibleCats = latestCats.filter(c => {
        if (c.is_active === false) return false;
        if (isHidden(c.name || '')) return false;
        return itemCatNames.has((c.name || '').toLowerCase().trim());
      });
      setItems(visibleItems);
      setCats(visibleCats);
      setLoadingMenu(false);
    };

    const unsubItems = onSnapshot(collection(db, 'menu_items'), (snap) => {
      latestItems = snap.docs.map(d => {
        const data = d.data() as any;
        return {
          id: d.id,
          name: data.name || '',
          description: data.description || '',
          price: parseFloat(data.price) || 0,
          category: data.category || '',
          image_url: data.image_url || '',
          is_available: data.is_available !== false,
          sold_out: !!data.sold_out,
          has_addons: !!data.has_addons,
          modifiers: data.modifiers || [],
          tax_rate: data.tax_rate,
          brand: data.brand,
          display_number: data.display_number,
        } as unknown as MenuItem;
      });
      applyFilters();
    }, (e) => { console.warn('menu_items snapshot', e); setLoadingMenu(false); });

    const unsubCats = onSnapshot(collection(db, 'categories'), (snap) => {
      const raw: Category[] = snap.docs.map(d => {
        const data = d.data() as any;
        return {
          id: d.id,
          name: data.name || '',
          icon: data.icon || '',
          image_url: data.image_url || '',
          is_active: data.is_active !== false,
          display_order: data.display_order || 0,
          display_order_by_brand: data.display_order_by_brand || null,
          item_count: 0,
          items: [],
        } as Category;
      });
      // Sort + deduplicate by name (same logic as getCategories)
      raw.sort((a, b) => (a.display_order || 0) - (b.display_order || 0) || a.name.localeCompare(b.name));
      const seen = new Set<string>();
      latestCats = raw.filter(c => {
        const key = (c.name || '').trim().toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      applyFilters();
    }, (e) => { console.warn('categories snapshot', e); });

    return () => { unsubItems(); unsubCats(); };
  }, []);

  // ── Phone autocomplete ─────────────────────────────────────────
  useEffect(() => {
    const q = customer.phone.replace(/\s/g, '');
    if (q.length < 2) { setSuggestions([]); setShowSuggest(false); return; }
    const matches = allCustomers
      .filter(c => c.phone.includes(q))
      .slice(0, 6);
    setSuggestions(matches);
    setShowSuggest(matches.length > 0);
  }, [customer.phone, allCustomers]);

  // ── Name autocomplete (fires on Name field — matches by name or company) ──
  const [nameSuggestions, setNameSuggestions] = useState<CustomerSuggestion[]>([]);
  const [showNameSuggest, setShowNameSuggest] = useState(false);
  useEffect(() => {
    const q = customer.name.trim().toLowerCase();
    if (q.length < 2) { setNameSuggestions([]); setShowNameSuggest(false); return; }
    const matches = allCustomers
      .filter(c => (c.name || '').toLowerCase().includes(q) || (c.company || '').toLowerCase().includes(q))
      .slice(0, 6);
    setNameSuggestions(matches);
    setShowNameSuggest(matches.length > 0);
  }, [customer.name, allCustomers]);

  // ── Company autocomplete (only when Company checkbox is on) ──
  const [companySuggestions, setCompanySuggestions] = useState<CustomerSuggestion[]>([]);
  const [showCompanySuggest, setShowCompanySuggest] = useState(false);
  useEffect(() => {
    const q = customer.company.trim().toLowerCase();
    if (!customer.isCompany || q.length < 2) {
      setCompanySuggestions([]); setShowCompanySuggest(false); return;
    }
    const matches = allCustomers
      .filter(c => (c.company || '').toLowerCase().includes(q))
      .slice(0, 6);
    setCompanySuggestions(matches);
    setShowCompanySuggest(matches.length > 0);
  }, [customer.company, customer.isCompany, allCustomers]);

  // Street autocomplete state (the actual effect that fills `streetSuggestions`
  // lives further down — it must wait for `mapLoaded`).
  const [streetSuggestions, setStreetSuggestions] = useState<Array<{ id: string; description: string; main: string; secondary: string; placeId: string }>>([]);
  const [showStreetSuggest, setShowStreetSuggest] = useState(false);

  const pickSuggestion = (s: CustomerSuggestion) => {
    const street = s.address.split(/\d+/)[0]?.trim() || s.address;
    const houseMatch = s.address.match(/(\d+\s*\w*)\s*$/);
    setCustomer(prev => ({
      ...prev,
      phone: s.phone,
      name: s.name,
      street: [street, s.postcode, s.city].filter(Boolean).join(' ').trim(),
      houseNumber: houseMatch ? houseMatch[1].trim() : '',
      customerNumber: s.customerNumber || s.id.slice(0, 6).toUpperCase(),
    }));
    setShowSuggest(false);
  };

  // Pick a customer suggestion from the Name dropdown — fills everything we
  // know about that customer (phone, address, company status).
  const pickNameSuggestion = (s: CustomerSuggestion) => {
    const street = s.address.split(/\d+/)[0]?.trim() || s.address;
    const houseMatch = s.address.match(/(\d+\s*\w*)\s*$/);
    setCustomer(prev => ({
      ...prev,
      phone: s.phone || prev.phone,
      name: s.name,
      company: s.company || '',
      isCompany: !!s.isCompany,
      street: [street, s.postcode, s.city].filter(Boolean).join(' ').trim() || prev.street,
      houseNumber: houseMatch ? houseMatch[1].trim() : prev.houseNumber,
      customerNumber: s.customerNumber || s.id.slice(0, 6).toUpperCase(),
    }));
    setShowNameSuggest(false);
  };
  const pickCompanySuggestion = (s: CustomerSuggestion) => {
    pickNameSuggestion(s); // identical fill — but the user typed in the Company box
    setShowCompanySuggest(false);
  };

  // Fill street field from Nominatim suggestion.
  const pickStreetSuggestion = async (s: { placeId: string; main: string; secondary: string }) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(s.main)}&limit=1&addressdetails=1`,
        { headers: { 'Accept-Language': 'en' } },
      );
      const data = await res.json();
      if (data?.[0]) {
        const a = data[0].address || {};
        const route = a.road || '';
        const postcode = a.postcode || '';
        const city = a.city || a.town || a.village || '';
        const streetNumber = a.house_number || '';
        setCustomer(prev => ({
          ...prev,
          street: [route, postcode, city].filter(Boolean).join(' ').trim(),
          houseNumber: streetNumber || prev.houseNumber,
        }));
        setDeliveryPos({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
      }
    } catch { /* non-critical */ }
    setShowStreetSuggest(false);
  };

  const resetCustomer = () => {
    setCustomer(prev => ({ ...EMPTY_CUSTOMER, keepFormOnScreen: prev.keepFormOnScreen }));
    setShowSuggest(false);
    setShowNameSuggest(false);
    setShowCompanySuggest(false);
    setShowStreetSuggest(false);
  };

  // ── Map (Leaflet/OSM) ────────────────────────────────────────────
  // Saheeh uses react-leaflet + OpenStreetMap — no Google API key needed.
  void true; // mapLoaded stub removed (Leaflet needs no async load)
  // Default center: Riyadh, Saudi Arabia
  const restaurantPos = useMemo(() => ({ lat: 24.7136, lng: 46.6753 }), []);
  const [deliveryPos, setDeliveryPos] = useState<{ lat: number; lng: number } | null>(null);

  // ALL / Driver-only toggle (top-right of map)
  const [mapFilter, setMapFilter] = useState<'all' | 'drivers'>('all');

  // Map theme — no-op placeholder to satisfy existing mapTheme references below
  const [mapTheme] = useState<'dark' | 'light'>('dark');
  useEffect(() => {
    try { localStorage.setItem('pos_map_theme', mapTheme); } catch {}
  }, [mapTheme]);

  // ── Street autocomplete via Nominatim ──
  useEffect(() => {
    const q = customer.street.trim();
    if (q.length < 3) { setStreetSuggestions([]); setShowStreetSuggest(false); return; }
    const t = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=6&addressdetails=1`,
          { headers: { 'Accept-Language': 'en' } },
        );
        const data = await res.json();
        const out = (data || []).map((p: any) => ({
          id: p.place_id,
          placeId: p.place_id,
          description: p.display_name,
          main: p.address?.road || p.display_name.split(',')[0],
          secondary: [p.address?.city || p.address?.town, p.address?.country].filter(Boolean).join(', '),
        }));
        setStreetSuggestions(out);
        setShowStreetSuggest(out.length > 0);
      } catch { setStreetSuggestions([]); setShowStreetSuggest(false); }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.street]);

  // Live driver positions (drivers currently online)
  const [drivers, setDrivers] = useState<Array<{
    id: string;
    name: string;
    code: string;
    lat: number;
    lng: number;
    status: 'idle' | 'busy';
    vehicle_type: string;
  }>>([]);
  // Live `drivers` collection — used to enrich live GPS positions with the
  // admin-configured `map_code` (label) and `vehicle_type` (emoji) since
  // those fields don't exist on `driver_locations`. Keyed by email AND id
  // so we can match either way the rider app stored its identifier.
  const [driverMeta, setDriverMeta] = useState<Record<string, { map_code?: string; vehicle_type?: string }>>({});
  useEffect(() => {
    const db = getFirestore();
    const unsub = onSnapshot(collection(db, 'drivers'), (snap) => {
      const map: Record<string, { map_code?: string; vehicle_type?: string }> = {};
      snap.forEach(d => {
        const data = d.data() as any;
        const entry = { map_code: data.map_code, vehicle_type: data.vehicle_type };
        map[d.id] = entry;
        if (data.email) map[String(data.email).toLowerCase()] = entry;
      });
      setDriverMeta(map);
    });
    return () => unsub();
  }, []);

  // Active delivery orders with geocoded coords — rendered as pins on the map.
  // Live subscription so new orders appear (and completed ones drop off)
  // without a page refresh. Filtered to current branch only.
  const [mapOrders, setMapOrders] = useState<Array<{ id: string; lat: number; lng: number; order_number: string; customer: string; address: string; status: string }>>([]);
  useEffect(() => {
    const db = getFirestore();
    const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
    const myBranchId = currentBranchId();
    const q = query(
      collection(db, 'orders'),
      where('created_at', '>=', startOfDay),
    );
    const unsub = onSnapshot(q, (snap) => {
      const list: any[] = [];
      snap.forEach(d => {
        const data = d.data() as any;
        const orderBranch = (data.branch_id || 'huerth') as string;
        if (orderBranch !== myBranchId) return;
        if ((data.order_type || 'delivery') !== 'delivery') return;
        const lat = Number(data.delivery_latitude);
        const lng = Number(data.delivery_longitude);
        if (!isFinite(lat) || !isFinite(lng) || (lat === 0 && lng === 0)) return;
        const st = String(data.status || 'pending').toLowerCase();
        if (['completed', 'delivered', 'cancelled'].includes(st)) return;
        list.push({
          id: d.id,
          lat, lng,
          order_number: data.order_number || `#${d.id.slice(-5)}`,
          customer: data.customer_name || 'Walk-in',
          address: data.delivery_address || data.customer_address || '',
          status: st,
        });
      });
      setMapOrders(list);
    });
    return () => unsub();
  }, []);
  useEffect(() => {
    const db = getFirestore();
    const unsub = onSnapshot(collection(db, 'driver_locations'), (snap) => {
      const list: any[] = [];
      let idx = 0;
      snap.forEach(d => {
        const data = d.data() as any;
        const lat = Number(data.latitude || data.current_latitude || 0);
        const lng = Number(data.longitude || data.current_longitude || 0);
        if (!lat || !lng) return;
        if (data.is_online === false) return;
        idx++;
        // Look up admin-set metadata (label + vehicle type) from the drivers
        // collection. Try by doc id first, then by email — different code
        // paths in the rider app have historically used either.
        const meta = driverMeta[d.id]
          || driverMeta[String(data.driver_email || '').toLowerCase()]
          || {};
        list.push({
          id: d.id,
          name: data.driver_name || data.driver_email || `Fahrer ${idx}`,
          code: meta.map_code || data.driver_code || `F${String(idx).padStart(2, '0')}`,
          lat, lng,
          status: data.current_order_id ? 'busy' : 'idle',
          vehicle_type: meta.vehicle_type || data.vehicle_type || '',
        });
      });
      setDrivers(list);
    });
    return () => unsub();
    // Re-subscribe when driverMeta changes so meta updates (admin rename of
    // map_code or vehicle_type) propagate to the live driver pins.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverMeta]);

  // Live subscription to settings/delivery_config — radius bands and
  // postal-code zones. The matching band/zone is then applied below as the
  // customer's address resolves. Listener is mounted once.
  const [deliveryConfig, setDeliveryConfig] = useState<any>(null);
  useEffect(() => {
    let unsub: (() => void) | undefined;
    (async () => {
      try {
        const { onSnapshot, doc, getFirestore } = await import('../../services/firestoreCompat');
        const db = getFirestore();
        unsub = onSnapshot(doc(db, 'settings', 'delivery_config'), (snap) => {
          setDeliveryConfig(snap.exists() ? snap.data() : null);
        });
      } catch { /* non-critical */ }
    })();
    return () => { if (unsub) unsub(); };
  }, []);

  // Geocode delivery address via Nominatim (debounced).
  useEffect(() => {
    const address = [customer.street, customer.houseNumber].filter(Boolean).join(' ').trim();
    if (!address || address.length < 4) { setDeliveryPos(null); return; }
    const t = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
          { headers: { 'Accept-Language': 'en' } },
        );
        const data = await res.json();
        if (data?.[0]) setDeliveryPos({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
      } catch { /* non-critical */ }
    }, 800);
    return () => clearTimeout(t);
  }, [customer.street, customer.houseNumber]);

  // ── Cart helpers ───────────────────────────────────────────────
  const cartSubtotal = useMemo(() =>
    cart.reduce((s, l) => s + l.qty * l.unitPrice, 0),
  [cart]);
  const discountAmount = useMemo(() =>
    Math.round((cartSubtotal * (discountPct || 0) / 100) * 100) / 100,
  [cartSubtotal, discountPct]);
  const cartTotal = useMemo(() =>
    Math.max(0, cartSubtotal - discountAmount + (Number(customer.deliveryFee) || 0)),
  [cartSubtotal, discountAmount, customer.deliveryFee]);

  // Auto-apply delivery fee from settings/delivery_config based on the
  // resolved address (and current time-of-day if the schedule is active).
  //   1. Zone mode → match by postal code in the customer's street
  //   2. Radius mode → haversine distance from the restaurant to the
  //      geocoded position, pick the smallest band whose distance_km
  //      bracket covers the actual distance
  //   3. Schedule active and current time in window → override with the
  //      schedule's delivery cost (e.g. lunchtime promo)
  useEffect(() => {
    if (!deliveryConfig || orderType !== 'delivery') return;
    const sub = cartSubtotal - discountAmount;
    const now = new Date();
    const moIdx = (now.getDay() + 6) % 7;
    const schedule = deliveryConfig.schedule;
    const scheduleApplies = schedule?.active
      && Array.isArray(schedule.days) && schedule.days[moIdx]
      && (() => {
        const [sh, sm] = String(schedule.start_time || '00:00').split(':').map(Number);
        const [eh, em] = String(schedule.end_time || '00:00').split(':').map(Number);
        const nowMin = now.getHours() * 60 + now.getMinutes();
        return nowMin >= sh * 60 + sm && nowMin <= eh * 60 + em;
      })();

    let nextFee: number | null = null;

    if (deliveryConfig.mode === 'zone' && Array.isArray(deliveryConfig.zones)) {
      const addr = customer.street || '';
      const plzMatch = addr.match(/\b(\d{4,5})\b/);
      const plz = plzMatch?.[1];
      if (plz) {
        const zone = deliveryConfig.zones.find((z: any) => String(z.postal_code) === plz);
        if (zone) {
          nextFee = (zone.delivery_free_from > 0 && sub >= zone.delivery_free_from)
            ? 0 : Number(zone.delivery_costs) || 0;
        }
      }
    } else if (deliveryConfig.mode === 'radius' && Array.isArray(deliveryConfig.bands) && deliveryPos) {
      const toRad = (d: number) => d * Math.PI / 180;
      const R = 6371;
      const dLat = toRad(deliveryPos.lat - restaurantPos.lat);
      const dLng = toRad(deliveryPos.lng - restaurantPos.lng);
      const a = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(restaurantPos.lat)) * Math.cos(toRad(deliveryPos.lat))
        * Math.sin(dLng / 2) ** 2;
      const distance = 2 * R * Math.asin(Math.sqrt(a));
      const eligible = (deliveryConfig.bands as any[])
        .filter(b => Number(b.distance_km) >= distance)
        .sort((a, b) => Number(a.distance_km) - Number(b.distance_km));
      if (eligible.length > 0) {
        const band = eligible[0];
        nextFee = (band.free_delivery_above > 0 && sub >= band.free_delivery_above)
          ? 0 : Number(band.delivery_cost) || 0;
      }
    }

    if (scheduleApplies) {
      nextFee = Number(schedule.delivery_cost) || 0;
    }

    if (nextFee !== null && nextFee !== customer.deliveryFee) {
      setCustomer(prev => ({ ...prev, deliveryFee: nextFee as number }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryConfig, deliveryPos, customer.street, cartSubtotal, discountAmount, orderType]);

  // Liefersoft-style: tapping a product card opens the add-ons modal first
  // so the cashier can pick toppings / notes / modifiers, then confirm with
  // F2. Items without any modifier_group_ids and no add-ons available will
  // still need a confirmation tap — that's intentional for visual parity.
  const [addonsModalOpen, setAddonsModalOpen] = useState(false);
  const [productForAddons, setProductForAddons] = useState<any>(null);

  // Payment-method dialog state. F2 / "Print & Save" no longer creates the
  // order directly — it opens this dialog first so the cashier can pick how
  // the customer is paying (Liefersoft parity). The order is then saved with
  // that method and payment_status reflects whether it was a paid-at-counter
  // method (cash/card) or a deferred one (invoice).
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentNotification, setPaymentNotification] = useState('');

  // Cart-config gating: live subscription to settings/cart_config so the
  // POS reacts to admin/settings changes within ~1s.
  //   - pager_active + pager_mandatory → pager-number prompt before save
  //   - request_amount_of_items[channel] → "Confirm X items" before save
  //   - show_change_window[channel] → change-calculator after Cash chosen
  const [cartConfig, setCartConfig] = useState<any>({
    pager_active: false, pager_mandatory: false,
    request_amount_of_items: { delivery: false, pickup: false, counter: false },
    show_change_window: { delivery: false, pickup: true, counter: true },
  });
  useEffect(() => {
    let unsub: (() => void) | undefined;
    (async () => {
      try {
        const { onSnapshot, doc, getFirestore } = await import('../../services/firestoreCompat');
        const db = getFirestore();
        unsub = onSnapshot(doc(db, 'settings', 'cart_config'), (snap) => {
          if (snap.exists()) {
            const data = snap.data() as any;
            setCartConfig((prev: any) => ({
              ...prev, ...data,
              request_amount_of_items: { ...prev.request_amount_of_items, ...(data.request_amount_of_items || {}) },
              show_change_window: { ...prev.show_change_window, ...(data.show_change_window || {}) },
            }));
          }
        });
      } catch { /* non-critical */ }
    })();
    return () => { if (unsub) unsub(); };
  }, []);

  // Pre-payment gate state
  const [pagerDialogOpen, setPagerDialogOpen] = useState(false);
  const [pagerNumber, setPagerNumber] = useState('');
  const [amountConfirmOpen, setAmountConfirmOpen] = useState(false);
  const [amountConfirmInput, setAmountConfirmInput] = useState('');
  // Post-Cash change calculator state
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [amountReceived, setAmountReceived] = useState('');
  const [tipAmount, setTipAmount] = useState('');

  // Cash desk extras
  const [openDrawerConfirm, setOpenDrawerConfirm] = useState(false);
  const [cartSheetOpen, setCartSheetOpen] = useState(false);
  const [dineInDialogOpen, setDineInDialogOpen] = useState(false);
  const [dineInTables, setDineInTables] = useState<any[]>([]);
  const [dineInSubmitting, setDineInSubmitting] = useState(false);
  const [moneyWithdrawOpen, setMoneyWithdrawOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawReason, setWithdrawReason] = useState('');
  const openItemPicker = (m: MenuItem) => {
    setProductForAddons(m);
    setAddonsModalOpen(true);
  };

  // Called by AddItemWithAddOnsModal when the cashier hits Add F2.
  const addToCartFromModal = (
    product: any,
    selectedAddOns: Array<{ id: string; name: string; price: number; quantity?: number }>,
    notes: string,
    extra?: { resolvedPrice?: number; selections?: { [groupId: string]: string[] }; modifierGroups?: any[] },
  ) => {
    const baseUnit = (extra?.resolvedPrice && extra.resolvedPrice > 0)
      ? extra.resolvedPrice
      : (product.price_with_tax || product.base_price || 0);
    const addonsTotal = selectedAddOns.reduce((s, a) => s + (a.price || 0) * (a.quantity || 1), 0);
    // When resolvedPrice is set, it already includes modifier deltas, so don't double-count.
    const unit = extra?.resolvedPrice ? baseUnit : baseUnit + addonsTotal;

    // Convert modifier-group selections (Dips, Toppings, …) into display
    // sub-lines for the cart. Skip the size group — its "delta" is the base
    // price for that size, not an extra topping. Size is reflected in unit
    // price; showing it as a sub-line with a price would mislead the cashier.
    const modifierAddons: Array<{ name: string; price: number; quantity: number }> = [];
    if (extra?.selections && extra?.modifierGroups) {
      const sizeGroup = extra.modifierGroups.find((g: any) => g.applies_to_size);
      const sizeOptionId: string | null = sizeGroup ? (extra.selections[sizeGroup.id]?.[0] || null) : null;
      extra.modifierGroups.forEach((g: any) => {
        if (g.applies_to_size) return;
        const picked: string[] = extra.selections![g.id] || [];
        picked.forEach((optId) => {
          const opt = g.options.find((o: any) => o.id === optId);
          if (!opt) return;
          const delta = (sizeOptionId && opt.price_delta_by_size && opt.price_delta_by_size[sizeOptionId] !== undefined)
            ? opt.price_delta_by_size[sizeOptionId]
            : (opt.price_delta || 0);
          const label = (isDe && opt.name_de) ? opt.name_de : opt.name;
          modifierAddons.push({ name: label, price: delta, quantity: 1 });
        });
      });
    }

    const legacyAddons = selectedAddOns.map(a => ({ name: a.name, price: a.price, quantity: a.quantity || 1 }));

    setCart(prev => [...prev, {
      id: `${product.id}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      itemId: product.id,
      name: product.name_de || product.name,
      qty: 1,
      unitPrice: unit,
      notes,
      addons: [...legacyAddons, ...modifierAddons].filter(a => a.name),
    }]);
    setAddonsModalOpen(false);
    setProductForAddons(null);
  };

  // (Old quick-add path removed — every product tap now goes through the
  // add-ons modal, just like Liefersoft.)

  const changeQty = (lineId: string, delta: number) => {
    setCart(prev => prev
      .map(l => l.id === lineId ? { ...l, qty: l.qty + delta } : l)
      .filter(l => l.qty > 0)
    );
  };

  const removeLine = (lineId: string) => setCart(prev => prev.filter(l => l.id !== lineId));

  // ── Product list filtered by category + search ─────────────────
  const filteredItems = useMemo(() => {
    let list = items;
    const q = productSearch.trim().toLowerCase();
    // When a search query is active, ignore category filter and search across all items
    if (!q && activeCats.size > 0) {
      // Prefix match: "Crispy Roll" also shows "Crispy Rolls" items, "Desserts" shows "Desserts & X"
      list = list.filter(m => {
        const cat = (m.category || '').toLowerCase().trim();
        return [...activeCats].some(sel => {
          const k = sel.toLowerCase().trim();
          return cat === k || cat.startsWith(k + ' ') || cat.startsWith(k + '-') || cat.startsWith(k + 's');
        });
      });
    }
    if (q) {
      // (1) Article-number search. Item names are stored with the number
      // prefix in the name itself (e.g. "133 Chicken-Crispy Roll"). If the
      // query is purely numeric, match against the leading article number
      // — that's the same number cashiers know by heart.
      const num = parseInt(q, 10);
      if (!isNaN(num) && String(num) === q) {
        // Exact prefix: "133 ..."
        const exact = list.filter(m => {
          const nm = (m.name || '').trim();
          return nm.startsWith(q + ' ') || nm.startsWith(q + '.') || nm === q;
        });
        if (exact.length > 0) return exact;
        // Fallback: substring on name only (covers "133", "0133", etc.)
        const sub = list.filter(m => (m.name || '').toLowerCase().includes(q));
        return sub;
      }

      // (2) Name-only search. Sadik flagged that searching "Edamame" was
      // returning every roll that listed edamame as an INGREDIENT. The cause
      // was matching against `description` too. Restrict to name + name_de
      // so search returns the actual product, not items that mention it.
      list = list.filter(m =>
        ((m.name || '') + ' ' + (m.name_de || '')).toLowerCase().includes(q)
      );
    }
    return list;
  }, [items, activeCats, productSearch]);

  // Open cash drawer — writes a print_jobs doc with job_type 'open_drawer'
  // for the Pi relay to send the Epson kick command (ESC p 0 25 250). If
  // the relay doesn't support it yet it's a no-op, and we still surface the
  // action in the audit trail so daily closing reconciles correctly.
  const fireOpenDrawer = async () => {
    setOpenDrawerConfirm(false);
    try {
      const db = getFirestore();
      await addDoc(collection(db, 'print_jobs'), {
        job_type: 'open_drawer',
        status: 'pending',
        relay_id: 'main',
        branch_id: currentBranchId(),
        created_at: serverTimestamp(),
      });
      setSnack({ open: true, sev: 'success', msg: isDe ? 'Kassenschublade geöffnet' : 'Cash drawer opened' });
    } catch (e: any) {
      setSnack({ open: true, sev: 'error', msg: e?.message || 'Failed to open drawer' });
    }
  };

  // Money withdraw — logs a `cash_movements` doc that Daily Closing
  // reconciles. Negative amount = cash leaving the till.
  const submitMoneyWithdraw = async () => {
    const amt = parseFloat(withdrawAmount.replace(',', '.'));
    if (isNaN(amt) || amt <= 0) {
      setSnack({ open: true, sev: 'error', msg: isDe ? 'Bitte gültigen Betrag eingeben' : 'Please enter a valid amount' });
      return;
    }
    try {
      const db = getFirestore();
      await addDoc(collection(db, 'cash_movements'), {
        type: 'withdraw',
        amount: -Math.abs(amt),
        reason: withdrawReason || (isDe ? 'Geldentnahme' : 'Withdraw'),
        branch_id: currentBranchId(),
        created_at: serverTimestamp(),
        created_by: 'pos',
      });
      setWithdrawAmount(''); setWithdrawReason(''); setMoneyWithdrawOpen(false);
      setSnack({ open: true, sev: 'success', msg: isDe ? 'Entnahme erfasst' : 'Withdraw recorded' });
    } catch (e: any) {
      setSnack({ open: true, sev: 'error', msg: e?.message || 'Failed to record withdraw' });
    }
  };

  // F2 / Print button entry point. Walks through the configured gates in
  // order before finally opening the payment method dialog:
  //   1. Cart not empty + customer details for delivery
  //   2. (cart_config) Pager dialog if pager_active
  //   3. (cart_config) Item-count confirmation if request_amount_of_items[channel]
  //   4. Payment method dialog
  const totalCartQty = useMemo(() => cart.reduce((s, l) => s + l.qty, 0), [cart]);

  const proceedToPayment = () => setPaymentDialogOpen(true);

  const proceedFromAmountConfirm = () => {
    const entered = parseInt(amountConfirmInput, 10);
    if (isNaN(entered) || entered !== totalCartQty) {
      setSnack({ open: true, sev: 'error', msg: isDe
        ? `Stückzahl stimmt nicht überein (${totalCartQty} im Warenkorb)`
        : `Item count doesn't match (${totalCartQty} in cart)` });
      return;
    }
    setAmountConfirmOpen(false);
    setAmountConfirmInput('');
    proceedToPayment();
  };

  const proceedFromPager = () => {
    if (cartConfig.pager_mandatory && !pagerNumber.trim()) {
      setSnack({ open: true, sev: 'error', msg: isDe ? 'Pager-Nummer ist erforderlich' : 'Pager number required' });
      return;
    }
    setPagerDialogOpen(false);
    // Next gate: item count
    if (cartConfig.request_amount_of_items?.[orderType]) {
      setAmountConfirmOpen(true);
    } else {
      proceedToPayment();
    }
  };

  const openPaymentDialog = () => {
    if (cart.length === 0) {
      setSnack({ open: true, sev: 'error', msg: isDe ? 'Warenkorb ist leer' : 'Cart is empty' });
      return;
    }
    if (orderType === 'delivery' && !customer.phone && !customer.name) {
      setSnack({ open: true, sev: 'error', msg: isDe ? 'Bitte Kundendaten eingeben' : 'Please enter customer details' });
      return;
    }
    // Gate 1: pager
    if (cartConfig.pager_active) {
      setPagerDialogOpen(true);
      return;
    }
    // Gate 2: amount confirm
    if (cartConfig.request_amount_of_items?.[orderType]) {
      setAmountConfirmOpen(true);
      return;
    }
    proceedToPayment();
  };

  // ── Save order on Print, with chosen payment method ─────────────
  const finalizeOrderWithPayment = async (paymentMethod: string) => {
    // Cash/Card are settled at the counter or on delivery; everything else
    // (PayPal/Apple Pay/Google Pay/Klarna/Sofort/Giropay) is treated as
    // already paid via the customer-side flow. Invoice is deferred.
    const paidNow = !['invoice'].includes(paymentMethod);
    const paymentStatus = paymentMethod === 'cash' || paymentMethod === 'card'
      ? 'pending' // collected at handover
      : paidNow ? 'paid' : 'pending';
    setPaymentDialogOpen(false);
    setSubmitting(true);
    try {
      const db = getFirestore();
      const orderItems = cart.map(l => ({
        menu_item_id: l.itemId,
        name: l.name,
        item_name: l.name,
        quantity: l.qty,
        unit_price: l.unitPrice,
        price: l.unitPrice,
        total: l.qty * l.unitPrice,
        notes: l.notes || '',
        is_extra: l.isExtra || false,
        addons: l.addons && l.addons.length > 0 ? l.addons : undefined,
      }));
      // Reserve a 5-digit sequential order number from the shared counter
      // (same as Restaurant/Dashboard paths). Falls back to a timestamp if
      // the counter transaction fails — the service handles that internally.
      let reservedOrderNumber = '';
      try {
        reservedOrderNumber = await FirebaseOrderService.getInstance().getNextOrderNumber();
      } catch {
        reservedOrderNumber = String(Date.now() % 100000).padStart(5, '0');
      }

      const orderDoc = {
        order_number: reservedOrderNumber,
        order_type: orderType,
        status: 'confirmed',
        items: orderItems,
        subtotal: cartSubtotal,
        discount_amount: discountAmount,
        discount_percent: discountPct,
        delivery_fee: Number(customer.deliveryFee) || 0,
        total: cartTotal,
        customer_name: customer.name || 'Walk-in',
        customer_phone: customer.phone,
        customer_number: customer.customerNumber || nextCustomerNumber,
        customer_company: customer.company,
        customer_is_company: customer.isCompany,
        customer_address: [customer.street, customer.houseNumber].filter(Boolean).join(' '),
        delivery_address: [customer.street, customer.houseNumber].filter(Boolean).join(' '),
        customer_comment: customer.comment,
        coupon_code: couponCode || null,
        scheduled_for: preorderDate ? preorderDate.toISOString() : null,
        delivery_eta_minutes: deliveryMinutes || null,
        source: 'pos',
        platform: 'pos',
        payment_method: paymentMethod,
        payment_status: paymentStatus,
        payment_notification: paymentNotification || '',
        pager_number: pagerNumber || '',
        branch_id: currentBranchId(),
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      };
      const docRef = await addDoc(collection(db, 'orders'), orderDoc);

      // Upsert customer for future autocomplete
      try {
        const customersRef = collection(db, 'customers');
        const existing = customer.phone
          ? await getDocs(query(customersRef, where('phone', '==', customer.phone)))
          : null;
        const customerData = {
          name: customer.name,
          phone: customer.phone,
          address: customer.street,
          company: customer.company,
          is_company: customer.isCompany,
          customer_number: customer.customerNumber || nextCustomerNumber,
          updated_at: serverTimestamp(),
        };
        if (existing && !existing.empty) {
          await updateDoc(doc(db, 'customers', existing.docs[0].id), customerData);
        } else if (customer.phone || customer.name) {
          await addDoc(customersRef, { ...customerData, created_at: serverTimestamp() });
        }
      } catch { /* non-critical */ }

      // Fire-and-forget cashier receipt print + kitchen ticket. Without the
      // kitchen call here the cashdesk flow only printed a cashier receipt
      // and the kitchen never knew what to make — surfaced on the Köln rollout
      // 2026-05-15. Mirrors the takeaway/delivery branch in MinimalistDashboard.
      try {
        const ps = FirestorePrintService.getInstance();
        const receiptData = {
          orderNumber: reservedOrderNumber || docRef.id,
          customerName: customer.name || 'Walk-in',
          customerAddress: [customer.street, customer.houseNumber].filter(Boolean).join(' '),
          customerPhone: customer.phone,
          orderDate: new Date().toLocaleDateString('de-DE'),
          orderTime: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
          items: orderItems.map((it, idx) => ({ ...it, itemNumber: idx + 1 })),
          subtotal: Math.round((cartSubtotal / 1.07) * 100) / 100,
          tax: Math.round((cartSubtotal - (cartSubtotal / 1.07)) * 100) / 100,
          total: cartTotal,
          paymentMethod,
          paymentStatus,
          order_type: orderType,
          delivery_fee: Number(customer.deliveryFee) || 0,
          ...(discountAmount > 0 ? { discount_amount: discountAmount, total_before_discount: cartSubtotal + (Number(customer.deliveryFee) || 0) } : {}),
        };
        await ps.printCashierReceipt(docRef.id, docRef.id, receiptData);
        // Kitchen ticket — non-blocking. Dine-in flow already prints kitchen
        // via Restaurant page; cashdesk path covers walk-in / takeaway / delivery.
        ps.printKitchenOrder(docRef.id, reservedOrderNumber || docRef.id, receiptData)
          .then(results => results.forEach(r => console.log('[PRINT] Kitchen job:', r.jobId, r.success ? 'ok' : r.message)))
          .catch(e => console.error('[PRINT] Kitchen print error (non-fatal):', e));
      } catch { /* relay errors are non-fatal */ }

      setSnack({ open: true, sev: 'success', msg: isDe ? 'Bestellung gespeichert & gedruckt' : 'Order saved & printed' });

      // Reset for next order
      setCart([]);
      setDiscountPct(0);
      setPreorderDate(null);
      setDeliveryMinutes(null);
      setCouponCode('');
      setPaymentNotification('');
      setPagerNumber('');
      setAmountReceived('');
      setTipAmount('');
      // Always return to the customer/main screen after saving. The
      // "keep form on screen" toggle only controls whether the previous
      // customer's details (phone/name/address) are preserved for fast
      // repeat orders — it should NOT trap the cashier on an empty cart.
      if (!customer.keepFormOnScreen) {
        resetCustomer();
      }
      // Cash desk has no customer form; staying on the cart is correct.
      if (!isCashDesk) setStep('customer');
    } catch (e: any) {
      console.error('[DELIVERY POS] save failed', e);
      setSnack({ open: true, sev: 'error', msg: isDe ? 'Fehler beim Speichern' : 'Save failed' });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Convert current cart to a dine-in / table order ────────────
  const openDineInDialog = async () => {
    setPaymentDialogOpen(false);
    setDineInSubmitting(false); // reset any stuck state from previous attempt
    setDineInDialogOpen(true);
    try {
      const db = getFirestore();
      const snap = await getDocs(
        query(collection(db, 'restaurant_tables'),
          where('status', '==', 'available'),
          where('branch_id', '==', currentBranchId()),
        )
      );
      const tables = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Also try without branch_id filter as fallback (older docs may not have it)
      if (tables.length === 0) {
        const snap2 = await getDocs(query(collection(db, 'restaurant_tables'), where('status', '==', 'available')));
        setDineInTables(snap2.docs.map(d => ({ id: d.id, ...d.data() })));
      } else {
        setDineInTables(tables);
      }
    } catch (e) {
      console.error('[DINE-IN] Failed to load tables:', e);
      setDineInTables([]);
    }
  };

  const handleSelectTable = async (table: any) => {
    if (dineInSubmitting) return;
    setDineInSubmitting(true);
    // Snapshot state values NOW before any async operations (avoids stale closure risk)
    const snapCart = cart;
    const snapTotal = cartTotal;
    const snapSubtotal = cartSubtotal;
    const snapDiscount = discountAmount;
    const snapDiscountPct = discountPct;
    try {
      const db = getFirestore();

      // Build items — strip undefined fields explicitly (don't use JSON.parse/stringify
      // on the whole doc because it corrupts serverTimestamp() sentinels)
      const orderItems = snapCart.map(l => {
        const item: any = {
          menu_item_id: l.itemId,
          name: l.name,
          item_name: l.name,
          quantity: l.qty,
          unit_price: l.unitPrice,
          price: l.unitPrice,
          total: l.qty * l.unitPrice,
          notes: l.notes || '',
          is_extra: l.isExtra || false,
        };
        if (l.addons && l.addons.length > 0) item.addons = l.addons;
        return item;
      });

      let reservedOrderNumber = '';
      try {
        reservedOrderNumber = await FirebaseOrderService.getInstance().getNextOrderNumber();
      } catch {
        reservedOrderNumber = String(Date.now() % 100000).padStart(5, '0');
      }

      const tableNum = table.number ?? table.table_number ?? '';
      const displayNum = table.display_number ?? tableNum;
      const tableArea = table.area || 'indoor';
      const totalQty = snapCart.reduce((s, l) => s + l.qty, 0);
      const autoName = isDe ? `Dine-In Tisch ${displayNum}` : `Dine-In Table ${displayNum}`;

      // Build order doc directly — no JSON.parse/stringify, serverTimestamp() stays as sentinel
      const orderDoc: any = {
        order_number: reservedOrderNumber,
        order_type: 'dine_in',
        status: 'confirmed',
        items: orderItems,
        subtotal: snapSubtotal,
        discount_amount: snapDiscount,
        discount_percent: snapDiscountPct,
        total: snapTotal,
        table_number: String(tableNum),
        table_id: table.id,
        table_area: tableArea,
        display_number: displayNum,
        customer_name: customer.name || autoName,
        customer_phone: customer.phone || '',
        source: 'pos',
        platform: 'pos',
        payment_method: '',
        payment_status: 'pending',
        branch_id: currentBranchId(),
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        converted_from: orderType,
      };
      const docRef = await addDoc(collection(db, 'orders'), orderDoc);
      const orderId = docRef.id;

      // Mark table as occupied + link active order.
      // Write ALL total/count fields the restaurant page may read.
      await updateDoc(doc(db, 'restaurant_tables', table.id), {
        status: 'occupied',
        customer_name: autoName,
        active_order_id: orderId,
        active_order_number: reservedOrderNumber,
        start_time: new Date().toISOString(),
        // Both field names used by different rendering paths in ModernRestaurantPage
        total_amount: snapTotal,
        tab_total: snapTotal,
        item_count: totalQty,
        tab_item_count: totalQty,
        tab_status: 'open',
        tab_open: true,
      });

      // Save table_context so dashboard picks up this table immediately
      const tableContextData: any = {
        id: String(table.id),
        number: tableNum,
        display_number: displayNum,
        type: 'dine_in',
        area: tableArea,
        active_order_id: orderId,
        active_order_number: reservedOrderNumber,
        start_time: new Date().toISOString(),
      };
      localStorage.setItem('selectedTable', JSON.stringify(tableContextData));
      await setDoc(doc(db, 'table_context', 'current'), {
        selectedTable: tableContextData,
        updated_at: new Date().toISOString(),
      }, { merge: true });

      // Print kitchen ticket so kitchen starts cooking immediately
      try {
        const ps = FirestorePrintService.getInstance();
        const kitchenData = {
          orderNumber: reservedOrderNumber,
          tableNumber: String(displayNum),
          items: orderItems.map((it, idx) => ({ ...it, itemNumber: idx + 1 })),
          total: snapTotal,
          order_type: 'dine_in',
        };
        await ps.printKitchenOrder(orderId, reservedOrderNumber, kitchenData);
      } catch { /* non-fatal */ }

      // Reset cart
      setCart([]);
      setDiscountPct(0);
      setPreorderDate(null);
      setDeliveryMinutes(null);
      setCouponCode('');
      setPaymentNotification('');
      setPagerNumber('');
      setDineInDialogOpen(false);

      setSnack({ open: true, sev: 'success', msg: isDe ? `Tisch ${displayNum} geöffnet` : `Table ${displayNum} opened` });
      setTimeout(() => navigate('/dashboard'), 600);
    } catch (e: any) {
      console.error('[DINE-IN] Convert failed:', e);
      setSnack({ open: true, sev: 'error', msg: isDe ? 'Fehler beim Übertragen' : 'Convert failed' });
    } finally {
      setDineInSubmitting(false);
    }
  };

  const goToCart = (type: 'delivery' | 'pickup' | 'counter' = 'delivery') => {
    // ALL three order paths require at least a phone or a name. Pickup and
    // Counter often print a name on the receipt for the cashier to call out,
    // so we don't let them through with an empty form either.
    if (!customer.phone.trim() && !customer.name.trim()) {
      setSnack({ open: true, sev: 'error', msg: isDe ? 'Bitte Kundendaten eingeben' : 'Please enter customer details' });
      return;
    }
    setOrderType(type);
    setStep('cart');
  };

  // ── Keyboard shortcuts (F2/F3/ESC) ─────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'F2' && step === 'customer') { e.preventDefault(); goToCart(); }
      else if (e.key === 'F2' && step === 'cart') { e.preventDefault(); openPaymentDialog(); }
      else if (e.key === 'Escape' && step === 'cart' && !isCashDesk) { e.preventDefault(); setStep('customer'); }
      else if (e.key === 'F8' && step === 'cart') { e.preventDefault(); setPreorderOpen(true); }
      else if (e.key === 'F3' && step === 'cart') { e.preventDefault(); setExtrasOpen(true); }
      else if (e.key === 'F6' && step === 'cart') { e.preventDefault(); setDeliveryTimeOpen(true); }
      else if (e.key === 'F4' && step === 'cart') {
        e.preventDefault();
        setCart([]); setDiscountPct(0); setCouponCode('');
        setPreorderDate(null); setDeliveryMinutes(null); setPaymentNotification('');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, customer, cart, cartTotal]);

  // ────────────────────────────────────────────────────────────────
  // ───────────────────── RENDER ──────────────────────────────────
  // ────────────────────────────────────────────────────────────────

  if (step === 'customer') {
    return (
      <Box sx={{ p: { xs: 1, md: 1.5 }, display: 'flex', flexDirection: 'column', gap: 1.2, width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>

        {/* Top action bar — Pickup / Counter / Incoming call / Pickup Panel (Liefersoft parity).
            These are visual entry points; tapping currently routes the cashier to the cart screen
            with the matching order_type so they're functional even before a fully-featured Pickup
            mode is built out. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1 }}>
          <Button onClick={() => goToCart('pickup')} sx={topActionBtn('#3B82F6')} startIcon={<PickupIcon />}>
            {isDe ? 'Abholung' : 'Pickup'}
            <Box component="span" sx={{ ...kbdChip, display: { xs: 'none', md: 'inline-flex' } }}>F5</Box>
          </Button>
          <Button onClick={() => goToCart('counter')} sx={topActionBtn('#7C3AED')} startIcon={<StoreIcon />}>
            {isDe ? 'Theke' : 'Counter'}
            <Box component="span" sx={{ ...kbdChip, display: { xs: 'none', md: 'inline-flex' } }}>F6</Box>
          </Button>
          <Button onClick={() => { /* stub: hook up phone CTI later */ }} sx={topActionBtn('#22C55E')} startIcon={<PhoneIcon />}>
            {isDe ? 'Anruf' : 'Incoming call'}
            <Box component="span" sx={{ ...kbdChip, display: { xs: 'none', md: 'inline-flex' } }}>F3</Box>
          </Button>
          <Button onClick={() => navigate('/delivery/pickup-panel')} sx={topActionBtn('#374151')} startIcon={<TableIcon />}>
            {isDe ? 'Abholtafel' : 'Pickup Panel'}
          </Button>
        </Box>

        {/* Map area — fixed height like Liefersoft; form floats over it.
            The order-list strip below flows naturally with the page scroll. */}
        <Box sx={{
          position: 'relative', height: { xs: 'auto', md: 540 }, flex: '0 0 auto',
          borderRadius: { xs: '16px', md: '20px' }, overflow: { xs: 'visible', md: 'hidden' }, border: `1px solid ${C.border}`,
        }}>
          {/* Map layer — hidden on mobile, form takes full height */}
          <Box sx={{ position: 'absolute', inset: 0, display: { xs: 'none', md: 'block' } }}>
            {/* Leaflet/OSM map — no API key needed */}
            <MapContainer
              center={[deliveryPos?.lat ?? restaurantPos.lat, deliveryPos?.lng ?? restaurantPos.lng]}
              zoom={deliveryPos ? 16 : 13}
              style={{ width: '100%', height: '100%' }}
              key={`${deliveryPos?.lat ?? 0}-${deliveryPos?.lng ?? 0}`}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="© OpenStreetMap contributors"
              />
              {/* Restaurant pin */}
              <CircleMarker
                center={[restaurantPos.lat, restaurantPos.lng]}
                radius={12}
                pathOptions={{ fillColor: '#94e815', color: '#5cb800', fillOpacity: 1, weight: 2 }}
              >
                <LeafletTooltip permanent direction="center">🏪</LeafletTooltip>
              </CircleMarker>

              {/* Active delivery order pins */}
              {mapFilter !== 'drivers' && mapOrders.map(o => (
                <CircleMarker
                  key={`order-${o.id}`}
                  center={[o.lat, o.lng]}
                  radius={14}
                  pathOptions={{ fillColor: '#94e815', color: '#1a1d2e', fillOpacity: 0.95, weight: 2 }}
                >
                  <LeafletTooltip permanent direction="center">
                    {String(o.order_number).replace(/^#/, '').slice(-3)}
                  </LeafletTooltip>
                </CircleMarker>
              ))}

              {/* Current form delivery address pin */}
              {deliveryPos && mapFilter === 'all' && (
                <CircleMarker
                  center={[deliveryPos.lat, deliveryPos.lng]}
                  radius={12}
                  pathOptions={{ fillColor: '#ffb400', color: '#1a1d2e', fillOpacity: 1, weight: 2 }}
                >
                  <LeafletTooltip permanent direction="center">📍</LeafletTooltip>
                </CircleMarker>
              )}

              {/* Live driver pins */}
              {mapFilter !== 'all' && drivers.map(d => {
                const vt = (d.vehicle_type || '').toLowerCase();
                const vehicleIcon = vt === 'bicycle' || vt === 'bike' ? '🚴' : vt === 'car' ? '🚗' : '🛵';
                return (
                  <CircleMarker
                    key={d.id}
                    center={[d.lat, d.lng]}
                    radius={18}
                    pathOptions={{ fillColor: d.status === 'busy' ? '#F97316' : '#3B82F6', color: '#fff', fillOpacity: 0.95, weight: 2 }}
                  >
                    <LeafletTooltip permanent direction="center">{vehicleIcon} {d.code}</LeafletTooltip>
                  </CircleMarker>
                );
              })}
            </MapContainer>

            {/* ALL / Drivers-only toggle — desktop only */}
            <Box sx={{
              position: 'absolute', top: 60, right: 12, zIndex: 5,
              display: { xs: 'none', md: 'flex' }, backgroundColor: '#252838',
              border: `1.5px solid ${C.border}`, borderRadius: '14px',
              overflow: 'hidden', boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
            }}>
              <Box onClick={() => setMapFilter('all')} sx={{
                px: 2, py: 1, cursor: 'pointer', fontWeight: 800, fontSize: '0.95rem',
                color: mapFilter === 'all' ? C.text : C.textMuted,
                backgroundColor: mapFilter === 'all' ? C.accent + '22' : 'transparent',
                borderRight: `1.5px solid ${C.border}`,
                ...(mapFilter === 'all' ? { color: C.accent } : {}),
              }}>ALL</Box>
              <Box onClick={() => setMapFilter('drivers')} sx={{
                px: 1.8, py: 1, cursor: 'pointer',
                display: 'flex', alignItems: 'center',
                color: mapFilter === 'drivers' ? C.accent : C.textMuted,
                backgroundColor: mapFilter === 'drivers' ? C.accent + '22' : 'transparent',
              }}>
                <DeliveryIcon sx={{ fontSize: 22 }} />
              </Box>
            </Box>
          </Box>

          {/* Form panel overlaid on the left — sized to its content, no scroll.
              Liefersoft behaviour: when "Keep on screen" is OFF, the panel
              fades to a translucent state once the cashier moves their pointer
              off it, so the map underneath is readable. Hovering / focusing
              inside it brings the panel back to full opacity. When the toggle
              is ON, the panel is locked to fully visible no matter what. */}
          <Box
            onMouseEnter={() => setFormActive(true)}
            onMouseLeave={() => setFormActive(false)}
            onFocusCapture={() => setFormActive(true)}
            onBlurCapture={(e) => {
              // Only deactivate when focus leaves the entire panel
              const next = e.relatedTarget as Node | null;
              if (!next || !e.currentTarget.contains(next)) setFormActive(false);
            }}
            sx={{
              position: { xs: 'relative', md: 'absolute' },
              top: { xs: 0, md: 12 },
              left: { xs: 0, md: 12 },
              width: { xs: '100%', md: 460 },
              backgroundColor: { xs: C.surface, md: 'transparent' },
              border: { xs: `1px solid ${C.border}`, md: 'none' },
              borderRadius: { xs: '14px', md: 0 },
              p: { xs: 1.5, md: 0 },
              display: 'flex', flexDirection: 'column', gap: 0,
              boxShadow: { xs: '0 4px 20px rgba(0,0,0,0.3)', md: 'none' },
              opacity: { xs: 1, md: customer.keepFormOnScreen || formActive ? 1 : 0.45 },
              transition: 'opacity 0.25s ease',
            }}
          >
          {/* (Header removed — fields float on the map without a panel chrome,
              matching Liefersoft's layout.) */}

          {/* Phone with autocomplete — gets "Required" badge until either
              phone or name is filled (only one is needed to create an order). */}
          <Box sx={{ position: 'relative', mb: 1.8 }}>
          {needsAnyCustomerField && (
            <Box sx={requiredBadge}>{isDe ? 'Pflichtfeld' : 'Required'}</Box>
          )}
            <TextField
              label={isDe ? 'Telefonnummer' : 'Phone number'}
              value={customer.phone}
              onChange={e => setCustomer(p => ({ ...p, phone: e.target.value }))}
              onFocus={() => customer.phone.length >= 2 && setShowSuggest(suggestions.length > 0)}
              fullWidth
              autoFocus
              InputLabelProps={{ shrink: true }}
              sx={needsAnyCustomerField ? { ...inputSx, ...requiredInputSx } : inputSx}
            />
            {showSuggest && suggestions.length > 0 && (
              <Box sx={{
                position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20,
                backgroundColor: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.4)', overflow: 'hidden',
                maxHeight: 320, overflowY: 'auto',
              }}>
                {suggestions.map(s => (
                  <Box key={s.id} onClick={() => pickSuggestion(s)} sx={{
                    px: 2, py: 1.5, cursor: 'pointer',
                    borderBottom: `1px solid ${C.borderSubtle}`,
                    '&:hover': { backgroundColor: 'rgba(148,232,21,0.10)' },
                    '&:last-child': { borderBottom: 'none' },
                  }}>
                    <Typography sx={{ color: C.accent, fontFamily: 'monospace', fontSize: '0.95rem' }}>{s.phone}</Typography>
                    <Typography sx={{ color: C.text, fontWeight: 600 }}>{s.name || '(no name)'}</Typography>
                    {s.address && <Typography sx={{ color: C.textDim, fontSize: '0.85rem' }}>{s.address}</Typography>}
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Customer number — auto-generated on save if left blank */}
          <TextField
            label={isDe ? 'Kundennummer' : 'Customer number'}
            value={customer.customerNumber}
            onChange={e => setCustomer(p => ({ ...p, customerNumber: e.target.value }))}
            fullWidth
            InputLabelProps={{ shrink: true }}
            placeholder={isDe ? `Auto: ${nextCustomerNumber}` : `Auto: ${nextCustomerNumber}`}
            sx={{
              ...inputSx,
              mb: 1.8,
              '& input::placeholder': { color: '#94a3b8', opacity: 0.7, fontStyle: 'italic' },
            }}
          />

          {/* Name + Company toggle */}
          <Box sx={{ display: 'flex', gap: 1, mb: 1.8, alignItems: 'center' }}>
            <Box sx={{ position: 'relative', flex: 1 }}>
            {needsAnyCustomerField && (
              <Box sx={requiredBadge}>{isDe ? 'Pflichtfeld' : 'Required'}</Box>
            )}
            <TextField
              label={isDe ? 'Name' : 'Name'}
              value={customer.name}
              onChange={e => setCustomer(p => ({ ...p, name: e.target.value }))}
              onFocus={() => customer.name.length >= 2 && setShowNameSuggest(nameSuggestions.length > 0)}
              fullWidth InputLabelProps={{ shrink: true }}
              sx={needsAnyCustomerField ? { ...inputSx, ...requiredInputSx } : inputSx}
            />
            {showNameSuggest && nameSuggestions.length > 0 && (
              <Box sx={suggestionDropdown}>
                {nameSuggestions.map(s => (
                  <Box key={s.id} onClick={() => pickNameSuggestion(s)} sx={suggestionRow}>
                    <Typography sx={{ color: C.text, fontWeight: 600 }}>
                      {s.isCompany && '🏢 '}{s.name || '(no name)'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Typography sx={{ color: C.accent, fontFamily: 'monospace', fontSize: '0.85rem' }}>{s.phone}</Typography>
                      {s.address && <Typography sx={{ color: C.textDim, fontSize: '0.8rem' }}>· {s.address}</Typography>}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
            </Box>
            <Box
              onClick={() => setCustomer(p => ({ ...p, isCompany: !p.isCompany }))}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                color: C.text, fontSize: '0.9rem',
                cursor: 'pointer',
                px: 1, py: 0.6, borderRadius: '10px',
                backgroundColor: customer.isCompany
                  ? 'rgba(148,232,21,0.18)'
                  : 'rgba(26,29,46,0.85)',
                border: `1.5px solid ${customer.isCompany ? C.accent : C.border}`,
                backdropFilter: 'blur(2px)',
              }}
            >
              <Checkbox checked={customer.isCompany} onChange={() => {}} sx={{ color: C.textDim, '&.Mui-checked': { color: C.accent }, p: 0.3 }} />
              <Typography sx={{
                color: '#FFFFFF',
                fontWeight: 700,
                fontSize: '0.95rem',
                textShadow: '0 0 6px rgba(0,0,0,0.85), 0 1px 2px rgba(0,0,0,0.9)',
              }}>{isDe ? 'Firma' : 'Company'}</Typography>
            </Box>
          </Box>

          {/* Company field — only when "Firma / Company" is checked */}
          {customer.isCompany && (
            <Box sx={{ position: 'relative', mb: 1.8 }}>
              <TextField
                label={isDe ? 'Firmenname' : 'Company name'}
                value={customer.company}
                onChange={e => setCustomer(p => ({ ...p, company: e.target.value }))}
                onFocus={() => customer.company.length >= 2 && setShowCompanySuggest(companySuggestions.length > 0)}
                fullWidth InputLabelProps={{ shrink: true }} sx={inputSx}
              />
              {showCompanySuggest && companySuggestions.length > 0 && (
                <Box sx={suggestionDropdown}>
                  {companySuggestions.map(s => (
                    <Box key={s.id} onClick={() => pickCompanySuggestion(s)} sx={suggestionRow}>
                      <Typography sx={{ color: C.accent, fontWeight: 700, fontSize: '0.85rem' }}>🏢 {s.company || s.name}</Typography>
                      <Typography sx={{ color: C.text, fontSize: '0.85rem' }}>{s.name}</Typography>
                      {s.address && <Typography sx={{ color: C.textDim, fontSize: '0.78rem' }}>{s.address}</Typography>}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Street + House number */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: 1, mb: 1.8 }}>
            <Box sx={{ position: 'relative' }}>
              <TextField
                label={isDe ? 'Straße' : 'Street'}
                value={customer.street}
                onChange={e => setCustomer(p => ({ ...p, street: e.target.value }))}
                onFocus={() => customer.street.length >= 3 && setShowStreetSuggest(streetSuggestions.length > 0)}
                fullWidth InputLabelProps={{ shrink: true }} sx={inputSx}
              />
              {showStreetSuggest && streetSuggestions.length > 0 && (
                <Box sx={suggestionDropdown}>
                  {streetSuggestions.map(s => (
                    <Box key={s.id} onClick={() => pickStreetSuggestion(s)} sx={suggestionRow}>
                      <Typography sx={{ color: C.text, fontWeight: 600 }}>{s.main}</Typography>
                      {s.secondary && <Typography sx={{ color: C.textDim, fontSize: '0.8rem' }}>{s.secondary}</Typography>}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            <TextField
              label={isDe ? 'Hausnummer' : 'House number'}
              value={customer.houseNumber}
              onChange={e => setCustomer(p => ({ ...p, houseNumber: e.target.value }))}
              fullWidth InputLabelProps={{ shrink: true }} sx={inputSx}
            />
          </Box>

          {/* Comment + Delivery fees */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: 1, mb: 1.2 }}>
            <TextField
              label={isDe ? 'Kommentar' : 'Comment'}
              value={customer.comment}
              onChange={e => setCustomer(p => ({ ...p, comment: e.target.value }))}
              fullWidth multiline maxRows={3} InputLabelProps={{ shrink: true }} sx={inputSx}
            />
            <TextField
              label={isDe ? 'Liefergebühr' : 'Delivery fee'}
              value={customer.deliveryFee}
              onChange={e => setCustomer(p => ({ ...p, deliveryFee: Number(e.target.value) || 0 }))}
              type="number" fullWidth InputLabelProps={{ shrink: true }} sx={inputSx}
              InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ color: C.textDim }}>€</Typography></InputAdornment> }}
            />
          </Box>

          {/* Keep on screen — slim checkbox row matching Liefersoft.
              Preference is persisted to localStorage so the cashier's choice
              survives reloads. */}
          <Box
            onClick={() => setCustomer(p => {
              const next = !p.keepFormOnScreen;
              try { localStorage.setItem('pos_keep_form_on_screen', next ? '1' : '0'); } catch {}
              return { ...p, keepFormOnScreen: next };
            })}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              px: 0.5, py: 0.3, mb: 1, cursor: 'pointer',
              userSelect: 'none',
              color: C.textMuted,
              '&:hover .MuiCheckbox-root': { color: C.accent },
            }}
          >
            <Checkbox
              checked={customer.keepFormOnScreen}
              onChange={() => {}}
              sx={{
                color: C.textDim,
                '&.Mui-checked': { color: C.accent },
                p: 0.3,
              }}
            />
            <Typography sx={{
              color: '#FFFFFF',
              fontWeight: 700,
              fontSize: '0.85rem',
              textShadow: '0 0 6px rgba(0,0,0,0.85), 0 1px 2px rgba(0,0,0,0.9)',
            }}>
              {isDe ? 'Formular auf Bildschirm behalten' : 'Keep the form on screen'}
            </Typography>
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
            <Button onClick={resetCustomer} startIcon={<CloseIcon />} sx={{
              flex: '0 0 auto', backgroundColor: '#374151', color: '#FFFFFF',
              borderRadius: '12px', px: 2.4, py: 1, textTransform: 'none', fontWeight: 700, fontSize: '0.9rem',
              '&:hover': { backgroundColor: '#1F2937' },
            }}>{isDe ? 'Löschen' : 'Delete'}
              <Box component="span" sx={kbdChip}>ESC</Box>
            </Button>
            <Button onClick={() => goToCart('delivery')} sx={{
              flex: 1, backgroundColor: C.accent, color: '#1a1d2e',
              borderRadius: '12px', py: 1, textTransform: 'none', fontWeight: 800, fontSize: '0.98rem',
              boxShadow: '0 4px 14px rgba(148,232,21,0.35)',
              '&:hover': { backgroundColor: C.accentDark },
            }}>{isDe ? 'Bestellen' : 'Order'}
              <Box component="span" sx={kbdChipDark}>F2</Box>
            </Button>
          </Box>
        </Box>
        {/* /form panel */}
        </Box>
        {/* /map+form canvas */}

        {/* Recent delivery orders list — like Liefersoft footer */}
        <RecentDeliveryOrders isDe={isDe} />
      </Box>
    );
  }

  // ─────────────────── CART STEP ───────────────────
  return (
    <Box sx={{ p: { xs: 1, md: 1.5 }, pb: { xs: cart.length > 0 ? '90px' : 1, md: 1.5 }, display: 'flex', flexDirection: 'column', gap: 1.5, height: { xs: 'auto', md: '100vh' }, minHeight: { xs: 'calc(100vh - 24px)', md: 'unset' }, width: '100%', boxSizing: 'border-box', overflowX: 'hidden', overflow: { xs: 'visible', md: 'hidden' } }}>

      {/* ─── Cash desk header bar (Liefersoft parity) ───────────────
          Row 1: Counter | Pickup toggle on the left, Open cash drawer +
          Pickup Panel on the right. Auto-accept toggle lives in the
          sidebar (global setting), not here. */}
      {isCashDesk && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap',
          backgroundColor: C.surface, border: `1px solid ${C.border}`, borderRadius: '14px', px: 1.5, py: 1,
        }}>
          <IconButton onClick={() => navigate('/home')} sx={{ color: C.accent, flexShrink: 0 }}><BackIcon /></IconButton>
          {/* Counter/Pickup segmented pill — icon-only on mobile to save space */}
          <Box sx={{ display: 'flex', backgroundColor: '#1a1d2e', border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden', flexShrink: 0 }}>
            <Button
              onClick={() => setOrderType('counter')}
              startIcon={<StoreIcon sx={{ fontSize: { xs: 18, md: 20 } }} />}
              sx={{
                px: { xs: 0.8, md: 2 }, py: 0.8, borderRadius: 0, textTransform: 'none', fontWeight: 700, minWidth: 0,
                backgroundColor: orderType === 'counter' ? `${C.accent}22` : 'transparent',
                color: orderType === 'counter' ? C.accent : C.textMuted,
                '&:hover': { backgroundColor: orderType === 'counter' ? `${C.accent}33` : 'rgba(255,255,255,0.05)' },
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>{isDe ? 'Theke' : 'Counter'}</Box>
            </Button>
            <Button
              onClick={() => setOrderType('pickup')}
              startIcon={<PickupIcon sx={{ fontSize: { xs: 18, md: 20 } }} />}
              sx={{
                px: { xs: 0.8, md: 2 }, py: 0.8, borderRadius: 0, textTransform: 'none', fontWeight: 700, minWidth: 0,
                backgroundColor: orderType === 'pickup' ? `${C.accent}22` : 'transparent',
                color: orderType === 'pickup' ? C.accent : C.textMuted,
                '&:hover': { backgroundColor: orderType === 'pickup' ? `${C.accent}33` : 'rgba(255,255,255,0.05)' },
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>{isDe ? 'Abholung' : 'Pickup'}</Box>
            </Button>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setOpenDrawerConfirm(true)} startIcon={<DrawerIcon />} sx={{ ...topBtn('#22C55E'), flexShrink: 0, minWidth: 0, px: { xs: 1, md: 1.6 } }}>
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{isDe ? 'Kassenschublade öffnen' : 'Open cash drawer'}</Box>
          </Button>
          <Button onClick={() => navigate('/delivery/pickup-panel')} startIcon={<TableIcon />} sx={{ ...topBtn('#374151'), flexShrink: 0 }}>
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{isDe ? 'Abholtafel' : 'Pickup Panel'}</Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Panel</Box>
          </Button>
        </Box>
      )}

      {/* Top toolbar: Pre order / Extras / All Orders / Coupon / Money withdraw.
          For Delivery this is the per-order toolbar (Pre order/Extras/Delivery
          time/Coupon); for Cash desk we swap Delivery time for All Orders +
          Money withdraw to match Liefersoft. */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: { xs: 0.8, md: 2 },
        flexWrap: { xs: 'nowrap', md: 'wrap' },
        overflowX: { xs: 'auto', md: 'visible' },
        '&::-webkit-scrollbar': { display: 'none' },
        backgroundColor: C.surface, border: `1px solid ${C.border}`, borderRadius: '14px', px: { xs: 1, md: 2 }, py: 1,
      }}>
        {!isCashDesk && (
          <>
            <IconButton onClick={() => setStep('customer')} sx={{ color: C.accent, flexShrink: 0 }}><BackIcon /></IconButton>
            <Box sx={{ minWidth: 0, flex: { xs: 'none', md: 1 }, flexShrink: 0, maxWidth: { xs: 140, md: 'none' } }}>
              <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '1.05rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {customer.name || (isDe ? 'Laufkunde' : 'Walk-in')}
              </Typography>
              <Typography sx={{ color: C.textMuted, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {[customer.street, customer.houseNumber].filter(Boolean).join(' ')} · {customer.phone || '—'}
              </Typography>
            </Box>
          </>
        )}
        <Button startIcon={<ScheduleIcon />} onClick={() => setPreorderOpen(true)} sx={{ ...topBtn(preorderDate ? C.accent : '#F97316'), flexShrink: 0 }}>
          {preorderDate ? preorderDate.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : (isDe ? 'Vorbestellung' : 'Pre order')}
          <Box component="span" sx={{ ...kbdChip, display: { xs: 'none', md: 'inline-flex' } }}>F8</Box>
        </Button>
        <Button startIcon={<ExtrasIcon />} onClick={() => setExtrasOpen(true)} sx={{ ...topBtn('#8B5CF6'), flexShrink: 0 }}>
          {isDe ? 'Extras' : 'Extras'}<Box component="span" sx={{ ...kbdChip, display: { xs: 'none', md: 'inline-flex' } }}>F3</Box>
        </Button>
        {isCashDesk ? (
          <Button onClick={() => navigate('/orders-legacy')} startIcon={<AllOrdersIcon />} sx={{ ...topBtn('#0891B2'), flexShrink: 0 }}>
            {isDe ? 'Alle Bestellungen' : 'All Orders'}
          </Button>
        ) : (
          <Button startIcon={<DeliveryIcon />} onClick={() => setDeliveryTimeOpen(true)} sx={{ ...topBtn(deliveryMinutes ? C.accent : '#0891B2'), flexShrink: 0 }}>
            {deliveryMinutes ? `${deliveryMinutes} min` : (isDe ? 'Lieferzeit' : 'Delivery time')}
            <Box component="span" sx={{ ...kbdChip, display: { xs: 'none', md: 'inline-flex' } }}>F6</Box>
          </Button>
        )}
        <Button startIcon={<CouponIcon />} onClick={() => setCouponOpen(true)} sx={{ ...topBtn(couponCode ? C.accent : '#6366F1'), flexShrink: 0 }}>
          {couponCode || (isDe ? 'Gutschein' : 'Coupon')}
        </Button>
        {isCashDesk && (
          <Button onClick={() => setMoneyWithdrawOpen(true)} startIcon={<WithdrawIcon />} sx={{ ...topBtn('#EF4444'), flexShrink: 0 }}>
            {isDe ? 'Geldentnahme' : 'Money withdraw'}
          </Button>
        )}
      </Box>

      {/* 3-column body */}
      <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '380px 1fr 200px' }, gap: 1.5 }}>

        {/* LEFT: cart — on mobile shows last */}
        <Box sx={{ order: { xs: 3, md: 0 }, minWidth: 0, overflow: 'hidden', backgroundColor: C.surface, border: `1px solid ${C.border}`, borderRadius: '14px', display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 }, maxHeight: { xs: 380, md: 'none' } }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '68px 1fr 52px 58px 26px', md: '40px 1fr 60px 70px 30px' }, px: 1.5, py: 1, borderBottom: `1px solid ${C.border}`, color: C.textMuted, fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            <Box>{isDe ? 'Anz' : 'Qty'}</Box>
            <Box>{isDe ? 'Name' : 'Name'}</Box>
            <Box sx={{ textAlign: 'right' }}>{isDe ? 'Preis' : 'Price'}</Box>
            <Box sx={{ textAlign: 'right' }}>{isDe ? 'Summe' : 'Total'}</Box>
            <Box />
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', px: 0.5, py: 0.5 }}>
            {cart.length === 0 ? (
              <Box sx={{ color: C.textDim, textAlign: 'center', py: 6, fontSize: '0.95rem' }}>
                {isDe ? 'Warenkorb ist leer' : 'Cart is empty'}
              </Box>
            ) : cart.map(l => {
              const addons = (l as any).addons || [];
              const notes = (l as any).notes || '';
              return (
                <Box key={l.id} sx={{ borderBottom: `1px solid ${C.borderSubtle}` }}>
                  <Box sx={{
                    display: 'grid', gridTemplateColumns: { xs: '68px 1fr 52px 58px 26px', md: '40px 1fr 60px 70px 30px' }, alignItems: 'center',
                    px: 1.5, py: 1,
                    '&:hover': { backgroundColor: 'rgba(148,232,21,0.05)' },
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                      <IconButton size="small" onClick={() => changeQty(l.id, -1)} sx={qtyBtn}><RemoveIcon sx={{ fontSize: 14 }} /></IconButton>
                      <Typography sx={{ color: C.text, fontWeight: 700, width: 16, textAlign: 'center' }}>{l.qty}</Typography>
                      <IconButton size="small" onClick={() => changeQty(l.id, +1)} sx={qtyBtn}><AddIcon sx={{ fontSize: 14 }} /></IconButton>
                    </Box>
                    <Typography sx={{ color: C.text, fontSize: '0.92rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {l.name}{l.isExtra && <Typography component="span" sx={{ color: C.textDim, fontSize: '0.75rem', ml: 0.5 }}>(extra)</Typography>}
                    </Typography>
                    <Typography sx={{ color: C.textMuted, fontSize: '0.85rem', textAlign: 'right' }}>{l.unitPrice.toFixed(2)} €</Typography>
                    <Typography sx={{ color: C.accent, fontWeight: 700, textAlign: 'right' }}>{(l.qty * l.unitPrice).toFixed(2)} €</Typography>
                    <IconButton size="small" onClick={() => removeLine(l.id)} sx={{ color: C.danger, '&:hover': { color: '#fff', backgroundColor: C.danger } }}>
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                  {/* Add-on sub-rows — indented under the main item like Liefersoft */}
                  {addons.map((a: any, i: number) => (
                    <Box key={`${l.id}_addon_${i}`} sx={{
                      display: 'grid', gridTemplateColumns: { xs: '68px 1fr 52px 58px 26px', md: '40px 1fr 60px 70px 30px' },
                      alignItems: 'center', px: 1.5, py: 0.4,
                    }}>
                      <Box />
                      <Typography sx={{ color: C.textMuted, fontSize: '0.82rem', pl: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AddIcon sx={{ fontSize: 12, color: C.textDim }} />
                        {a.name}{a.quantity > 1 && <Typography component="span" sx={{ color: C.accent, fontWeight: 700, ml: 0.5 }}>x{a.quantity}</Typography>}
                      </Typography>
                      <Box />
                      <Typography sx={{ color: C.textDim, fontSize: '0.78rem', textAlign: 'right' }}>
                        {((a.price || 0) * (a.quantity || 1)) > 0
                          ? `${((a.price || 0) * (a.quantity || 1)).toFixed(2)} €`
                          : ''}
                      </Typography>
                      <Box />
                    </Box>
                  ))}
                  {notes && (
                    <Box sx={{ px: 1.5, py: 0.4, pl: 7, color: C.textDim, fontSize: '0.78rem', fontStyle: 'italic' }}>
                      {notes}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Cart footer: discount + totals + actions */}
          <Box sx={{ borderTop: `1px solid ${C.border}`, p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ color: C.textMuted, fontSize: '0.9rem' }}>{isDe ? 'Zwischensumme' : 'Subtotal'}</Typography>
              <Typography sx={{ color: C.text, fontWeight: 600 }}>{cartSubtotal.toFixed(2)} €</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ color: C.textMuted, fontSize: '0.9rem' }}>{isDe ? 'Rabatt' : 'Discount'}</Typography>
                <TextField type="number" value={discountPct} onChange={e => { setDiscountPct(Math.max(0, Math.min(100, Number(e.target.value) || 0))); setDiscountAutoApplied(false); }} size="small"
                  InputProps={{ endAdornment: <InputAdornment position="end" sx={{ color: C.textDim }}>%</InputAdornment> }}
                  sx={{ ...inputSx, width: 90, '& .MuiOutlinedInput-input': { color: `${C.text} !important`, py: 0.5, fontSize: '0.85rem' } }}
                />
              </Box>
              <Typography sx={{ color: C.danger, fontWeight: 600 }}>−{discountAmount.toFixed(2)} €</Typography>
            </Box>
            {customer.deliveryFee > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ color: C.textMuted, fontSize: '0.9rem' }}>{isDe ? 'Liefergebühr' : 'Delivery fee'}</Typography>
                <Typography sx={{ color: C.text, fontWeight: 600 }}>+{customer.deliveryFee.toFixed(2)} €</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 0.5, borderTop: `1px dashed ${C.border}` }}>
              <Typography sx={{ color: C.text, fontWeight: 800, fontSize: '1.05rem' }}>{isDe ? 'Gesamt' : 'Total'}</Typography>
              <Typography sx={{ color: C.accent, fontWeight: 800, fontSize: '1.25rem' }}>{cartTotal.toFixed(2)} €</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.8 }}>
              <Button
                onClick={() => {
                  setCart([]); setDiscountPct(0); setCouponCode('');
                  setPreorderDate(null); setDeliveryMinutes(null); setPaymentNotification('');
                }}
                disabled={submitting || cart.length === 0}
                sx={{
                  flex: { xs: 1, md: '0 0 30%' }, backgroundColor: '#2a2d40', color: '#cbd5e1',
                  borderRadius: '12px', py: 1.4, textTransform: 'none', fontWeight: 700, fontSize: '0.95rem',
                  border: '1px solid #3a3d52',
                  '&:hover': { backgroundColor: '#3a3d52' },
                  '&.Mui-disabled': { opacity: 0.4 },
                }}
              >
                {isDe ? 'Löschen' : 'Clear'}<Box component="span" sx={{ ...kbdChipDark, display: { xs: 'none', md: 'inline-flex' } }}>F4</Box>
              </Button>
              {/* Print button — hidden on mobile, sticky footer handles it */}
              <Button
                onClick={openPaymentDialog}
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={18} sx={{ color: '#1a1d2e' }} /> : <PrintIcon />}
                sx={{
                  flex: 1, backgroundColor: C.accent, color: '#1a1d2e',
                  borderRadius: '12px', py: 1.4, textTransform: 'none', fontWeight: 800, fontSize: '1rem',
                  boxShadow: '0 4px 14px rgba(148,232,21,0.35)',
                  display: { xs: 'none', md: 'flex' },
                  '&:hover': { backgroundColor: C.accentDark },
                  '&.Mui-disabled': { backgroundColor: C.accent, opacity: 0.6, color: '#1a1d2e' },
                }}
              >
                {isCashDesk ? (isDe ? 'Drucken' : 'Print') : (isDe ? 'Drucken & Speichern' : 'Print & Save')}<Box component="span" sx={kbdChipDark}>F2</Box>
              </Button>
            </Box>
          </Box>
        </Box>

        {/* CENTER: product picker — on mobile shows first */}
        <Box sx={{ order: { xs: 1, md: 0 }, minWidth: 0, backgroundColor: C.surface, border: `1px solid ${C.border}`, borderRadius: '14px', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {/* Mobile-only horizontal category scroll — replaces the right column on small screens */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, overflowX: 'auto', gap: 0.8, px: 1, py: 0.8, borderBottom: `1px solid ${C.border}`, flexShrink: 0, '&::-webkit-scrollbar': { display: 'none' } }}>
            <Box onClick={() => setActiveCats(new Set())} sx={{ flexShrink: 0, px: 1.5, py: 0.5, borderRadius: '20px', border: `1.5px solid ${activeCats.size === 0 ? C.accent : C.border}`, backgroundColor: activeCats.size === 0 ? `${C.accent}22` : C.surfaceAlt, color: activeCats.size === 0 ? C.accent : C.text, fontWeight: activeCats.size === 0 ? 700 : 500, fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {isDe ? 'Alle' : 'All'}
            </Box>
            {cats.filter(c => !!c.name && c.name.trim().length > 0).map(c => {
              const isActive = activeCats.has(c.name);
              return (
                <Box key={c.id || c.name} onClick={() => toggleCat(c.name)} sx={{ flexShrink: 0, px: 1.5, py: 0.5, borderRadius: '20px', border: `1.5px solid ${isActive ? C.accent : C.border}`, backgroundColor: isActive ? `${C.accent}22` : C.surfaceAlt, color: isActive ? C.accent : C.text, fontWeight: isActive ? 700 : 500, fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {c.name}
                </Box>
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.2, borderBottom: `1px solid ${C.border}` }}>
            <TextField
              placeholder={isDe ? 'Produkt suchen oder Nummer eingeben…' : 'Search product or type number…'}
              value={productSearch}
              onChange={e => setProductSearch(e.target.value)}
              fullWidth size="small"
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: C.textDim }} /></InputAdornment> }}
              sx={{
                ...inputSx,
                '& .MuiOutlinedInput-input': { color: `${C.text} !important`, py: 1, fontSize: '0.95rem' },
              }}
            />
            <IconButton onClick={() => setViewMode(v => v === 'grid' ? 'list' : 'grid')} sx={{ color: C.accent, border: `1px solid ${C.border}`, borderRadius: '10px' }}>
              {viewMode === 'grid' ? <ListIcon /> : <GridIcon />}
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
            {loadingMenu ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: C.accent }} /></Box>
            ) : filteredItems.length === 0 ? (
              <Box sx={{ color: C.textDim, textAlign: 'center', py: 6, fontSize: '0.95rem' }}>
                {isDe ? 'Keine Artikel gefunden' : 'No items found'}
              </Box>
            ) : viewMode === 'grid' ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', xl: 'repeat(4, 1fr)' }, gap: 1.2 }}>
                {filteredItems.map((m, idx) => (
                  <Box key={m.id} onClick={() => openItemPicker(m)} sx={{
                    backgroundColor: C.surfaceAlt, border: `1.5px solid ${C.border}`, borderRadius: '12px',
                    overflow: 'hidden', cursor: 'pointer', display: 'flex', flexDirection: 'column',
                    transition: 'all 0.15s ease',
                    '&:hover': { borderColor: C.accent, transform: 'translateY(-2px)', boxShadow: '0 6px 18px rgba(148,232,21,0.20)' },
                  }}>
                    <Box sx={{ position: 'relative', aspectRatio: '4/3', backgroundColor: '#1a1d2e' }}>
                      <MenuImage src={m.image_url} alt={m.name} category={m.category} />

                      <Box sx={{ position: 'absolute', top: 6, left: 6, backgroundColor: 'rgba(0,0,0,0.6)', color: C.text, px: 0.8, py: 0.2, borderRadius: '6px', fontSize: '0.7rem', fontWeight: 700 }}>
                        {idx + 1}
                      </Box>
                      <Box sx={{ position: 'absolute', bottom: 6, right: 6, backgroundColor: C.accent, color: '#1a1d2e', px: 1, py: 0.3, borderRadius: '8px', fontWeight: 800, fontSize: '0.85rem' }}>
                        {(m.price_with_tax || m.base_price || 0).toFixed(2)} €
                      </Box>
                    </Box>
                    <Box sx={{ p: 1, flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography sx={{ color: C.text, fontWeight: 600, fontSize: '0.88rem', lineHeight: 1.2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {m.name_de || m.name}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box>
                {filteredItems.map((m, idx) => (
                  <Box key={m.id} onClick={() => openItemPicker(m)} sx={{
                    display: 'grid', gridTemplateColumns: '40px 1fr 90px 40px', alignItems: 'center', gap: 1.5,
                    px: 1.5, py: 1.2, borderBottom: `1px solid ${C.borderSubtle}`, cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(148,232,21,0.08)' },
                  }}>
                    <Typography sx={{ color: C.textDim, fontFamily: 'monospace' }}>{idx + 1}</Typography>
                    <Typography sx={{ color: C.text, fontWeight: 500 }}>{m.name_de || m.name}</Typography>
                    <Typography sx={{ color: C.accent, fontWeight: 700, textAlign: 'right' }}>{(m.price_with_tax || m.base_price || 0).toFixed(2)} €</Typography>
                    <IconButton size="small" sx={{ backgroundColor: C.accent, color: '#1a1d2e', '&:hover': { backgroundColor: C.accentDark } }}><AddIcon sx={{ fontSize: 16 }} /></IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {/* RIGHT: category pills — hidden on mobile (replaced by horizontal strip above product grid) */}
        <Box sx={{ order: { xs: 2, md: 0 }, display: { xs: 'none', md: 'flex' }, backgroundColor: C.surface, border: `1px solid ${C.border}`, borderRadius: '14px', flexDirection: 'column', minHeight: 0 }}>
          {/* "All" sticky on top — primary selection */}
          <Box sx={{ p: 1.2, borderBottom: `1px solid ${C.border}` }}>
            <Box onClick={() => setActiveCats(new Set())} sx={catPill(activeCats.size === 0)}>
              <Typography sx={{ color: 'inherit', fontWeight: 700, fontSize: '0.95rem', textAlign: 'center', width: '100%' }}>
                {isDe ? 'Alle' : 'All'}
              </Typography>
            </Box>
          </Box>
          {/* Section label */}
          <Box sx={{ px: 1.2, pt: 1.2, pb: 0.5 }}>
            <Typography sx={{ color: C.textDim, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: 'center' }}>
              {isDe ? 'Kategorien' : 'All categories'}
            </Typography>
          </Box>
          {/* Category list — only entries with a real name; empty-string rows are skipped. */}
          <Box sx={{ flex: 1, overflowY: 'auto', px: 1.2, pb: 1.2, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            {cats
              .filter(c => !!c.name && c.name.trim().length > 0)
              .map(c => {
                const isActive = activeCats.has(c.name);
                return (
                  <Box
                    key={c.id || c.name}
                    onClick={() => toggleCat(c.name)}
                    sx={catPill(isActive)}
                  >
                    <Typography sx={{
                      color: isActive ? `${C.accent} !important` : `${C.text} !important`,
                      fontWeight: isActive ? 700 : 500,
                      fontSize: '0.85rem',
                      textAlign: 'center',
                      width: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {c.name}
                    </Typography>
                  </Box>
                );
              })}
            {cats.filter(c => !!c.name && c.name.trim()).length === 0 && (
              <Typography sx={{ color: C.textDim, textAlign: 'center', fontSize: '0.85rem', mt: 2 }}>
                {isDe ? 'Keine Kategorien' : 'No categories'}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* ─── Preorder dialog ─────────────────────────────── */}
      <Dialog open={preorderOpen} onClose={() => setPreorderOpen(false)} PaperProps={{ sx: modalPaper }}>
        <DialogContent sx={{ p: 3, width: { xs: 'calc(100vw - 64px)', sm: 360 }, maxWidth: '100%' }}>
          <Box sx={modalHeader}>
            <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '1.15rem' }}>{isDe ? 'Vorbestellung' : 'Pre order'}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={kbdChip}>ESC</Box>
              <IconButton onClick={() => setPreorderOpen(false)} sx={{ color: C.accent }}><CloseIcon /></IconButton>
            </Box>
          </Box>
          <TextField
            type="datetime-local"
            label={isDe ? 'Datum & Uhrzeit' : 'Date & time'}
            value={preorderDate ? preorderDate.toISOString().slice(0, 16) : ''}
            onChange={e => setPreorderDate(e.target.value ? new Date(e.target.value) : null)}
            fullWidth InputLabelProps={{ shrink: true }} sx={{ ...inputSx, mb: 2 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Button onClick={() => { setPreorderDate(null); setPreorderOpen(false); }} sx={cancelBtn}>{isDe ? 'Löschen' : 'Clear'}</Button>
            <Button onClick={() => setPreorderOpen(false)} sx={confirmBtn}>{isDe ? 'Bestätigen' : 'Confirm'}<Box component="span" sx={kbdChipDark}>F2</Box></Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ─── Delivery time picker ────────────────────────── */}
      <Dialog open={deliveryTimeOpen} onClose={() => setDeliveryTimeOpen(false)} PaperProps={{ sx: modalPaper }}>
        <DialogContent sx={{ p: 3, width: { xs: 'calc(100vw - 64px)', sm: 320 }, maxWidth: '100%' }}>
          <Box sx={modalHeader}>
            <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '1.15rem' }}>{isDe ? 'Lieferzeit' : 'Delivery time'}</Typography>
            <IconButton onClick={() => setDeliveryTimeOpen(false)} sx={{ color: C.accent }}><CloseIcon /></IconButton>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
            {[15, 30, 45, 60, 70, 90].map(min => (
              <Button key={min} onClick={() => { setDeliveryMinutes(min); setDeliveryTimeOpen(false); }} sx={{
                py: 1.6, fontWeight: 800, fontSize: '1.05rem',
                backgroundColor: deliveryMinutes === min ? C.accent : C.surfaceAlt,
                color: deliveryMinutes === min ? '#1a1d2e' : C.text,
                borderRadius: '12px', border: `1.5px solid ${deliveryMinutes === min ? C.accent : C.border}`,
                '&:hover': { backgroundColor: deliveryMinutes === min ? C.accentDark : 'rgba(148,232,21,0.10)', borderColor: C.accent },
              }}>{min} min</Button>
            ))}
          </Box>
        </DialogContent>
      </Dialog>

      {/* ─── Extras dialog ───────────────────────────────── */}
      <Dialog open={extrasOpen} onClose={() => setExtrasOpen(false)} PaperProps={{ sx: modalPaper }}>
        <DialogContent sx={{ p: 3, width: { xs: 'calc(100vw - 64px)', sm: 380 }, maxWidth: '100%' }}>
          <Box sx={modalHeader}>
            <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '1.15rem' }}>{isDe ? 'Extras' : 'Extras'}</Typography>
            <IconButton onClick={() => setExtrasOpen(false)} sx={{ color: C.accent }}><CloseIcon /></IconButton>
          </Box>
          <TextField label={isDe ? 'Bezeichnung' : 'Description'} value={extrasForm.description} onChange={e => setExtrasForm(p => ({ ...p, description: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} sx={{ ...inputSx, mb: 2 }} />
          <TextField type="number" label={isDe ? 'Preis' : 'Price'} value={extrasForm.price} onChange={e => setExtrasForm(p => ({ ...p, price: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} sx={{ ...inputSx, mb: 2 }} InputProps={{ endAdornment: <InputAdornment position="end" sx={{ color: C.textDim }}>€</InputAdornment> }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
            {(['Article','Beverage','Deposit','Tobacco'] as const).map(t => (
              <Button key={t} onClick={() => setExtrasForm(p => ({ ...p, type: t }))} sx={{
                py: 1.2, justifyContent: 'flex-start', textTransform: 'none', fontWeight: 600,
                backgroundColor: extrasForm.type === t ? C.accent : C.surfaceAlt,
                color: extrasForm.type === t ? '#1a1d2e' : C.text,
                border: `1.5px solid ${extrasForm.type === t ? C.accent : C.border}`, borderRadius: '10px',
              }}>{t}</Button>
            ))}
          </Box>
          <Button
            fullWidth
            onClick={() => {
              const price = Number(extrasForm.price) || 0;
              if (!extrasForm.description.trim()) return;
              setCart(prev => [...prev, {
                id: `extra_${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
                itemId: `extra_${Date.now()}`,
                name: `${extrasForm.description} (${extrasForm.type})`,
                qty: 1,
                unitPrice: price,
                isExtra: true,
              }]);
              setExtrasForm({ description: '', price: '', type: 'Article' });
              setExtrasOpen(false);
            }}
            sx={confirmBtn}
          >{isDe ? 'Hinzufügen' : 'Add'}</Button>
        </DialogContent>
      </Dialog>

      {/* ─── Coupon dialog ───────────────────────────────── */}
      <Dialog open={couponOpen} onClose={() => setCouponOpen(false)} PaperProps={{ sx: modalPaper }}>
        <DialogContent sx={{ p: 3, width: { xs: 'calc(100vw - 64px)', sm: 360 }, maxWidth: '100%' }}>
          <Box sx={modalHeader}>
            <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '1.15rem' }}>{isDe ? 'Gutschein-Nummer eingeben' : 'Enter coupon number'}</Typography>
            <IconButton onClick={() => setCouponOpen(false)} sx={{ color: C.accent }}><CloseIcon /></IconButton>
          </Box>
          <TextField value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())} fullWidth autoFocus InputLabelProps={{ shrink: true }} sx={{ ...inputSx, mb: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Button onClick={() => { setCouponCode(''); setCouponOpen(false); }} sx={cancelBtn}>{isDe ? 'Löschen' : 'Clear'}</Button>
            <Button onClick={() => setCouponOpen(false)} sx={confirmBtn}>{isDe ? 'Aktivieren' : 'Activate'}</Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ─── Payment method dialog ───────────────────────────
          Opens on F2 / Print. The cashier picks how the customer pays
          (Cash / Card / PayPal / Apple Pay / Google Pay / Klarna / Sofort /
          Giropay / Invoice) and the order is finalized with that method.
          Note: online methods only record the label — no money is charged
          here. Actual gateway integration (Braintree/Stripe) is separate. */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} PaperProps={{ sx: { ...modalPaper, maxWidth: 460, width: '100%' } }}>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={modalHeader}>
            <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '1.15rem' }}>
              {isDe ? 'Zahlungsmethode' : 'Payment method'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={kbdChip}>ESC</Box>
              <IconButton onClick={() => setPaymentDialogOpen(false)} sx={{ color: C.accent }}><CloseIcon /></IconButton>
            </Box>
          </Box>
          <Box sx={{ textAlign: 'center', mb: 2.5 }}>
            <Typography sx={{ color: C.textDim, fontSize: '0.95rem' }}>
              {isDe ? 'Zu zahlen:' : 'Pay:'}{' '}
              <Box component="span" sx={{ color: C.text, fontWeight: 700, fontSize: '1.4rem', ml: 1 }}>
                {cartTotal.toFixed(2).replace('.', ',')} €
              </Box>
            </Typography>
          </Box>
          <TextField
            value={paymentNotification}
            onChange={e => setPaymentNotification(e.target.value)}
            placeholder={isDe ? 'Notiz (optional)' : 'Notification (optional)'}
            fullWidth
            sx={{ ...inputSx, mb: 2 }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {([
              { key: 'cash', label: isDe ? 'Barzahlung' : 'Cash', color: '#22C55E' },
              { key: 'card', label: isDe ? 'Kartenzahlung' : 'Card', color: '#3B82F6' },
              { key: 'paypal', label: 'PayPal', color: '#3B82F6' },
              { key: 'invoice', label: isDe ? 'Rechnung' : 'Payment by invoice', color: '#A855F7' },
            ] as const).map(opt => (
              <Box
                key={opt.key}
                onClick={() => {
                  // Change/tip dialog is Cash-only and per-channel-gated.
                  if (opt.key === 'cash' && cartConfig.show_change_window?.[orderType]) {
                    setPaymentDialogOpen(false);
                    setChangeDialogOpen(true);
                    return;
                  }
                  finalizeOrderWithPayment(opt.key);
                }}
                sx={{
                  cursor: 'pointer', userSelect: 'none',
                  px: 2, py: 1.4, borderRadius: '10px',
                  border: `1.5px solid ${opt.color}55`,
                  backgroundColor: `${opt.color}15`,
                  color: opt.color, fontWeight: 700, fontSize: '0.95rem',
                  '&:hover': { backgroundColor: `${opt.color}28`, borderColor: opt.color },
                }}
              >
                {opt.label}
              </Box>
            ))}
            {/* Dine-In separator + option */}
            <Box sx={{ borderTop: `1px dashed ${C.border}`, mt: 0.5, pt: 1 }}>
              <Box
                onClick={openDineInDialog}
                sx={{
                  cursor: 'pointer', userSelect: 'none',
                  px: 2, py: 1.4, borderRadius: '10px',
                  border: '1.5px solid rgba(245,158,11,0.5)',
                  backgroundColor: 'rgba(245,158,11,0.12)',
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  '&:hover': { backgroundColor: 'rgba(245,158,11,0.22)', borderColor: '#F59E0B' },
                  transition: 'all 0.15s ease',
                }}
              >
                <TableIcon sx={{ color: '#F59E0B', fontSize: 22 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ color: '#F59E0B', fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.2 }}>
                    {isDe ? 'Zu Tisch umbuchen' : 'Move to Table / Dine-In'}
                  </Typography>
                  <Typography sx={{ color: 'rgba(245,158,11,0.7)', fontSize: '0.78rem' }}>
                    {isDe ? 'Tisch wählen — Zahlung am Tischende' : 'Select table — payment at end of dine-in'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ─── Dine-In table picker ─── */}
      <Dialog open={dineInDialogOpen} onClose={() => !dineInSubmitting && setDineInDialogOpen(false)} PaperProps={{ sx: { ...modalPaper, maxWidth: 520, width: '100%' } }}>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={modalHeader}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TableIcon sx={{ color: '#F59E0B', fontSize: 22 }} />
              <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '1.1rem' }}>
                {isDe ? 'Tisch wählen' : 'Select Table'}
              </Typography>
            </Box>
            <IconButton onClick={() => !dineInSubmitting && setDineInDialogOpen(false)} sx={{ color: C.accent }}><CloseIcon /></IconButton>
          </Box>
          <Typography sx={{ color: C.textDim, fontSize: '0.88rem', mb: 2 }}>
            {isDe
              ? `Warenkorb (${cart.reduce((s, l) => s + l.qty, 0)} Artikel · ${cartTotal.toFixed(2)} €) wird auf den gewählten Tisch übertragen.`
              : `Cart (${cart.reduce((s, l) => s + l.qty, 0)} items · ${cartTotal.toFixed(2)} €) will be transferred to the selected table.`}
          </Typography>
          {dineInTables.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: C.textDim }}>
              <TableIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
              <Typography>{isDe ? 'Keine freien Tische verfügbar' : 'No available tables'}</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 1.5, maxHeight: 340, overflowY: 'auto', pr: 0.5 }}>
              {dineInTables
                .sort((a, b) => (Number(a.display_number ?? a.number) || 0) - (Number(b.display_number ?? b.number) || 0))
                .map(table => {
                  const displayNum = table.display_number ?? table.number;
                  const area = table.area || 'indoor';
                  return (
                    <Box
                      key={table.id}
                      onClick={() => !dineInSubmitting && handleSelectTable(table)}
                      sx={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        gap: 0.5, py: 2, px: 1, borderRadius: '12px',
                        border: '1.5px solid rgba(245,158,11,0.35)',
                        backgroundColor: 'rgba(245,158,11,0.08)',
                        cursor: dineInSubmitting ? 'not-allowed' : 'pointer',
                        transition: 'all 0.15s ease',
                        '&:hover': { backgroundColor: 'rgba(245,158,11,0.2)', borderColor: '#F59E0B', transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(245,158,11,0.25)' },
                      }}
                    >
                      {dineInSubmitting ? (
                        <CircularProgress size={20} sx={{ color: '#F59E0B' }} />
                      ) : (
                        <TableIcon sx={{ color: '#F59E0B', fontSize: 28 }} />
                      )}
                      <Typography sx={{ color: C.text, fontWeight: 800, fontSize: '1.1rem' }}>
                        {isDe ? `Tisch ${displayNum}` : `Table ${displayNum}`}
                      </Typography>
                      <Typography sx={{ color: C.textMuted, fontSize: '0.72rem', textTransform: 'capitalize' }}>
                        {area}
                      </Typography>
                    </Box>
                  );
                })}
            </Box>
          )}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => !dineInSubmitting && setDineInDialogOpen(false)} sx={cancelBtn}>
              {isDe ? 'Abbrechen' : 'Cancel'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ─── Pager number dialog (cart_config: pager_active) ─── */}
      <Dialog open={pagerDialogOpen} onClose={() => setPagerDialogOpen(false)} PaperProps={{ sx: { ...modalPaper, maxWidth: 420 } }}>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={modalHeader}>
            <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '1.1rem' }}>
              {isDe ? 'Pager-Nummer' : 'Pager number'}
              {cartConfig.pager_mandatory && <Box component="span" sx={{ ml: 1, color: '#EF4444' }}>*</Box>}
            </Typography>
            <IconButton onClick={() => setPagerDialogOpen(false)} sx={{ color: C.accent }}><CloseIcon /></IconButton>
          </Box>
          <Typography sx={{ color: C.textDim, fontSize: '0.88rem', mb: 2 }}>
            {isDe ? 'Geben Sie die Pager-Nummer ein, die dem Kunden ausgehändigt wurde.' : 'Enter the pager number handed to the customer.'}
          </Typography>
          <TextField
            type="number"
            label={isDe ? 'Pager-Nr.' : 'Pager No.'}
            value={pagerNumber}
            onChange={e => setPagerNumber(e.target.value)}
            fullWidth autoFocus InputLabelProps={{ shrink: true }}
            sx={{ ...inputSx, mb: 2 }}
            inputProps={{ inputMode: 'numeric' }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Button onClick={() => setPagerDialogOpen(false)} sx={cancelBtn}>{isDe ? 'Abbrechen' : 'Cancel'}</Button>
            <Button onClick={proceedFromPager} sx={confirmBtn}>{isDe ? 'Weiter' : 'Continue'}</Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ─── Item count confirmation (cart_config: request_amount_of_items) ─── */}
      <Dialog open={amountConfirmOpen} onClose={() => setAmountConfirmOpen(false)} PaperProps={{ sx: { ...modalPaper, maxWidth: 420 } }}>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={modalHeader}>
            <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '1.1rem' }}>
              {isDe ? 'Stückzahl bestätigen' : 'Confirm item count'}
            </Typography>
            <IconButton onClick={() => setAmountConfirmOpen(false)} sx={{ color: C.accent }}><CloseIcon /></IconButton>
          </Box>
          <Typography sx={{ color: C.textDim, fontSize: '0.88rem', mb: 2 }}>
            {isDe
              ? `Bitte zählen Sie die Artikel und geben Sie die Gesamtanzahl ein.`
              : `Please count the items and enter the total amount.`}
          </Typography>
          <TextField
            type="number"
            label={isDe ? 'Anzahl Artikel' : 'Item count'}
            value={amountConfirmInput}
            onChange={e => setAmountConfirmInput(e.target.value)}
            fullWidth autoFocus InputLabelProps={{ shrink: true }}
            sx={{ ...inputSx, mb: 2 }}
            inputProps={{ inputMode: 'numeric', min: 1 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Button onClick={() => { setAmountConfirmOpen(false); setAmountConfirmInput(''); }} sx={cancelBtn}>{isDe ? 'Abbrechen' : 'Cancel'}</Button>
            <Button onClick={proceedFromAmountConfirm} sx={confirmBtn}>{isDe ? 'Bestätigen' : 'Confirm'}</Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ─── Change & tip calculator (cart_config: show_change_window for Cash) ─── */}
      <Dialog open={changeDialogOpen} onClose={() => setChangeDialogOpen(false)} PaperProps={{ sx: { ...modalPaper, maxWidth: 460, width: '100%' } }}>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={modalHeader}>
            <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '1.15rem' }}>
              {isDe ? 'Wechselgeld & Trinkgeld' : 'Change & tip'}
            </Typography>
            <IconButton onClick={() => setChangeDialogOpen(false)} sx={{ color: C.accent }}><CloseIcon /></IconButton>
          </Box>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography sx={{ color: C.textDim, fontSize: '0.95rem' }}>
              {isDe ? 'Zu zahlen:' : 'Pay:'}{' '}
              <Box component="span" sx={{ color: C.text, fontWeight: 700, fontSize: '1.4rem', ml: 1 }}>
                {cartTotal.toFixed(2).replace('.', ',')} €
              </Box>
            </Typography>
          </Box>
          <TextField
            type="number"
            label={isDe ? 'Erhaltener Betrag (€)' : 'Amount received (€)'}
            value={amountReceived}
            onChange={e => setAmountReceived(e.target.value)}
            fullWidth autoFocus InputLabelProps={{ shrink: true }}
            sx={{ ...inputSx, mb: 1.5 }}
            inputProps={{ inputMode: 'decimal', step: '0.01' }}
          />
          {/* Live change + tip computation */}
          {(() => {
            const received = parseFloat(amountReceived.replace(',', '.')) || 0;
            const tip = parseFloat(tipAmount.replace(',', '.')) || 0;
            const change = Math.max(0, received - cartTotal - tip);
            return (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
                <TextField
                  type="number"
                  label={isDe ? 'Trinkgeld (€)' : 'Tip (€)'}
                  value={tipAmount}
                  onChange={e => setTipAmount(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={inputSx}
                  inputProps={{ inputMode: 'decimal', step: '0.5' }}
                />
                <Box sx={{
                  display: 'flex', flexDirection: 'column', justifyContent: 'center',
                  px: 2, py: 1.2, borderRadius: '10px',
                  backgroundColor: change > 0 ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.04)',
                  border: `1.5px solid ${change > 0 ? '#22C55E' : '#3a3d52'}`,
                }}>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase' }}>
                    {isDe ? 'Wechselgeld' : 'Change'}
                  </Typography>
                  <Typography sx={{ color: change > 0 ? '#22C55E' : '#cbd5e1', fontWeight: 800, fontSize: '1.3rem' }}>
                    {change.toFixed(2).replace('.', ',')} €
                  </Typography>
                </Box>
              </Box>
            );
          })()}
          {/* Quick-fill buttons */}
          <Box sx={{ display: 'flex', gap: 0.8, mb: 2, flexWrap: 'wrap' }}>
            {[5, 10, 20, 50, 100].map(v => (
              <Button key={v} onClick={() => setAmountReceived(String(v))} sx={{
                backgroundColor: '#1a1d2e', color: '#cbd5e1', border: '1px solid #2a2d40',
                borderRadius: '8px', minWidth: 0, px: 1.5, py: 0.6, textTransform: 'none', fontWeight: 700,
                '&:hover': { borderColor: '#94e815', color: '#94e815' },
              }}>{v} €</Button>
            ))}
            <Button onClick={() => setAmountReceived(cartTotal.toFixed(2))} sx={{
              backgroundColor: '#1a1d2e', color: '#cbd5e1', border: '1px solid #2a2d40',
              borderRadius: '8px', px: 1.5, py: 0.6, textTransform: 'none', fontWeight: 700,
              '&:hover': { borderColor: '#94e815', color: '#94e815' },
            }}>{isDe ? 'Passend' : 'Exact'}</Button>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Button onClick={() => setChangeDialogOpen(false)} sx={cancelBtn}>{isDe ? 'Abbrechen' : 'Cancel'}</Button>
            <Button onClick={() => { setChangeDialogOpen(false); finalizeOrderWithPayment('cash'); }} sx={confirmBtn}>
              {isDe ? 'Bestätigen & Drucken' : 'Confirm & Print'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── Mobile sticky cart footer — always visible on mobile ── */}
      <Box sx={{
        display: { xs: 'flex', md: 'none' },
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1200,
        backgroundColor: '#1a1d2e', borderTop: `2px solid ${cart.length > 0 ? C.accent : C.border}`,
        px: 2, py: 1.2, alignItems: 'center', gap: 1.5,
        boxShadow: '0 -4px 24px rgba(0,0,0,0.5)',
        transition: 'border-color 0.2s ease',
      }}>
        {/* Tappable left area — opens cart sheet */}
        <Box
          onClick={() => cart.length > 0 && setCartSheetOpen(true)}
          sx={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 1,
            cursor: cart.length > 0 ? 'pointer' : 'default',
            borderRadius: '10px', px: 1, py: 0.5, mx: -1,
            transition: 'background 0.15s ease',
            ...(cart.length > 0 && { '&:active': { backgroundColor: 'rgba(148,232,21,0.08)' } }),
          }}
        >
          <CartIcon sx={{ color: cart.length > 0 ? C.accent : C.textMuted, fontSize: 22 }} />
          <Box>
            <Typography sx={{ color: C.textMuted, fontSize: '0.72rem', lineHeight: 1 }}>
              {cart.length > 0
                ? `${cart.reduce((s, l) => s + l.qty, 0)} ${isDe ? 'Artikel' : 'items'}`
                : (isDe ? 'Warenkorb leer' : 'Cart empty')}
            </Typography>
            <Typography sx={{ color: cart.length > 0 ? C.accent : C.textMuted, fontWeight: 800, fontSize: '1.2rem', lineHeight: 1.2 }}>
              {cartTotal.toFixed(2)} €
            </Typography>
          </Box>
          {cart.length > 0 && (
            <ChevronUpIcon sx={{ color: C.accent, fontSize: 20, ml: 0.5, opacity: 0.8 }} />
          )}
        </Box>
        <Button
          onClick={openPaymentDialog}
          disabled={submitting || cart.length === 0}
          startIcon={submitting ? <CircularProgress size={16} sx={{ color: '#1a1d2e' }} /> : <PrintIcon />}
          sx={{
            backgroundColor: cart.length > 0 ? C.accent : '#3a3d52', color: cart.length > 0 ? '#1a1d2e' : C.textMuted,
            borderRadius: '14px', py: 1.2, px: 3,
            textTransform: 'none', fontWeight: 800, fontSize: '0.95rem',
            boxShadow: cart.length > 0 ? '0 4px 14px rgba(148,232,21,0.4)' : 'none',
            transition: 'all 0.2s ease',
            '&:hover': { backgroundColor: cart.length > 0 ? C.accentDark : '#3a3d52' },
            '&.Mui-disabled': { backgroundColor: '#3a3d52', color: C.textMuted },
          }}
        >
          {isCashDesk ? (isDe ? 'Drucken' : 'Print') : (isDe ? 'Bestellen' : 'Order')}
        </Button>
      </Box>

      {/* ── Mobile cart bottom sheet ── */}
      <Drawer
        anchor="bottom"
        open={cartSheetOpen}
        onClose={() => setCartSheetOpen(false)}
        disableScrollLock
        PaperProps={{
          sx: {
            backgroundColor: '#1a1d2e',
            borderRadius: '20px 20px 0 0',
            maxHeight: '82vh',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
        sx={{ display: { xs: 'block', md: 'none' }, zIndex: 1300 }}
      >
        {/* Drag handle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.2, pb: 0.5 }}>
          <Box sx={{ width: 40, height: 4, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.2)' }} />
        </Box>

        {/* Sheet header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pb: 1.5, borderBottom: `1px solid ${C.border}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CartIcon sx={{ color: C.accent, fontSize: 22 }} />
            <Typography sx={{ color: C.text, fontWeight: 800, fontSize: '1.1rem' }}>
              {isDe ? 'Warenkorb' : 'Cart'}
            </Typography>
            <Box sx={{ backgroundColor: 'rgba(148,232,21,0.15)', border: `1px solid ${C.accent}`, borderRadius: '20px', px: 1, py: 0.1 }}>
              <Typography sx={{ color: C.accent, fontWeight: 700, fontSize: '0.78rem' }}>
                {cart.reduce((s, l) => s + l.qty, 0)}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setCartSheetOpen(false)} sx={{ color: C.textMuted, p: 0.5 }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Cart item list */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 1, py: 0.5 }}>
          {cart.length === 0 ? (
            <Box sx={{ color: C.textDim, textAlign: 'center', py: 6, fontSize: '0.95rem' }}>
              {isDe ? 'Warenkorb ist leer' : 'Cart is empty'}
            </Box>
          ) : cart.map(l => {
            const addons = (l as any).addons || [];
            const notes = (l as any).notes || '';
            return (
              <Box key={l.id} sx={{ borderBottom: `1px solid ${C.borderSubtle}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 1.2 }}>
                  {/* Qty controls */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <IconButton size="small" onClick={() => changeQty(l.id, -1)} sx={qtyBtn}>
                      <RemoveIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <Typography sx={{ color: C.text, fontWeight: 700, minWidth: 18, textAlign: 'center', fontSize: '0.95rem' }}>{l.qty}</Typography>
                    <IconButton size="small" onClick={() => changeQty(l.id, +1)} sx={qtyBtn}>
                      <AddIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                  {/* Name + price */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ color: C.text, fontSize: '0.92rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {l.name}
                      {l.isExtra && <Typography component="span" sx={{ color: C.textDim, fontSize: '0.75rem', ml: 0.5 }}>(extra)</Typography>}
                    </Typography>
                    <Typography sx={{ color: C.textMuted, fontSize: '0.8rem' }}>
                      {l.unitPrice.toFixed(2)} € × {l.qty}
                    </Typography>
                  </Box>
                  {/* Line total */}
                  <Typography sx={{ color: C.accent, fontWeight: 700, fontSize: '1rem', flexShrink: 0 }}>
                    {(l.qty * l.unitPrice).toFixed(2)} €
                  </Typography>
                  {/* Remove */}
                  <IconButton size="small" onClick={() => removeLine(l.id)} sx={{ color: C.danger, flexShrink: 0, '&:hover': { color: '#fff', backgroundColor: C.danger } }}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
                {/* Addons */}
                {addons.map((a: any, i: number) => (
                  <Box key={`${l.id}_addon_${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, pb: 0.6, pl: 6.5 }}>
                    <AddIcon sx={{ fontSize: 12, color: C.textDim, flexShrink: 0 }} />
                    <Typography sx={{ color: C.textMuted, fontSize: '0.8rem', flex: 1 }}>
                      {a.name}{a.quantity > 1 && <Typography component="span" sx={{ color: C.accent, fontWeight: 700, ml: 0.5 }}>×{a.quantity}</Typography>}
                    </Typography>
                    {((a.price || 0) * (a.quantity || 1)) > 0 && (
                      <Typography sx={{ color: C.textDim, fontSize: '0.78rem' }}>
                        +{((a.price || 0) * (a.quantity || 1)).toFixed(2)} €
                      </Typography>
                    )}
                  </Box>
                ))}
                {notes && (
                  <Box sx={{ px: 1, pb: 0.8, pl: 6.5, color: C.textDim, fontSize: '0.78rem', fontStyle: 'italic' }}>
                    {notes}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Sheet footer — discount + total + actions */}
        <Box sx={{ borderTop: `1px solid ${C.border}`, p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Discount row */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ color: C.textMuted, fontSize: '0.88rem' }}>{isDe ? 'Rabatt' : 'Discount'}</Typography>
              <TextField
                type="number"
                value={discountPct}
                onChange={e => { setDiscountPct(Math.max(0, Math.min(100, Number(e.target.value) || 0))); setDiscountAutoApplied(false); }}
                size="small"
                InputProps={{ endAdornment: <InputAdornment position="end" sx={{ color: C.textDim }}>%</InputAdornment> }}
                sx={{ ...inputSx, width: 80, '& .MuiOutlinedInput-input': { color: `${C.text} !important`, py: 0.5, fontSize: '0.85rem' } }}
              />
            </Box>
            <Typography sx={{ color: C.danger, fontWeight: 600 }}>−{discountAmount.toFixed(2)} €</Typography>
          </Box>
          {/* Total */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 0.5, borderTop: `1px dashed ${C.border}` }}>
            <Typography sx={{ color: C.text, fontWeight: 800, fontSize: '1.05rem' }}>{isDe ? 'Gesamt' : 'Total'}</Typography>
            <Typography sx={{ color: C.accent, fontWeight: 800, fontSize: '1.4rem' }}>{cartTotal.toFixed(2)} €</Typography>
          </Box>
          {/* Buttons */}
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <Button
              onClick={() => { setCart([]); setDiscountPct(0); setCouponCode(''); setPreorderDate(null); setDeliveryMinutes(null); setPaymentNotification(''); setCartSheetOpen(false); }}
              sx={{ flex: 1, color: C.danger, border: `1px solid ${C.danger}`, borderRadius: '12px', textTransform: 'none', fontWeight: 700, py: 1.2, '&:hover': { backgroundColor: 'rgba(239,68,68,0.1)' } }}
            >
              {isDe ? 'Leeren' : 'Clear'}
            </Button>
            <Button
              onClick={() => { setCartSheetOpen(false); openPaymentDialog(); }}
              disabled={submitting || cart.length === 0}
              startIcon={submitting ? <CircularProgress size={16} sx={{ color: '#1a1d2e' }} /> : <PrintIcon />}
              sx={{
                flex: 2, backgroundColor: C.accent, color: '#1a1d2e',
                borderRadius: '12px', textTransform: 'none', fontWeight: 800, py: 1.2, fontSize: '1rem',
                boxShadow: '0 4px 14px rgba(148,232,21,0.4)',
                '&:hover': { backgroundColor: C.accentDark },
                '&.Mui-disabled': { backgroundColor: '#3a3d52', color: C.textMuted, boxShadow: 'none' },
              }}
            >
              {isCashDesk ? (isDe ? 'Drucken' : 'Print') : (isDe ? 'Bestellen' : 'Order')}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* ─── Open cash drawer confirm ─────────────────────────── */}
      <Dialog open={openDrawerConfirm} onClose={() => setOpenDrawerConfirm(false)} PaperProps={{ sx: { ...modalPaper, maxWidth: 380 } }}>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={modalHeader}>
            <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '1.1rem' }}>{isDe ? 'Kassenschublade öffnen?' : 'Open cash drawer?'}</Typography>
            <IconButton onClick={() => setOpenDrawerConfirm(false)} sx={{ color: C.accent }}><CloseIcon /></IconButton>
          </Box>
          <Typography sx={{ color: C.textDim, fontSize: '0.9rem', mb: 2 }}>
            {isDe ? 'Die Aktion wird im Tagesabschluss protokolliert.' : 'This action is logged in the daily closing.'}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Button onClick={() => setOpenDrawerConfirm(false)} sx={cancelBtn}>{isDe ? 'Abbrechen' : 'Cancel'}</Button>
            <Button onClick={fireOpenDrawer} sx={confirmBtn}>{isDe ? 'Öffnen' : 'Open'}</Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ─── Money withdraw dialog ─────────────────────────────── */}
      <Dialog open={moneyWithdrawOpen} onClose={() => setMoneyWithdrawOpen(false)} PaperProps={{ sx: { ...modalPaper, maxWidth: 420, width: '100%' } }}>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={modalHeader}>
            <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '1.1rem' }}>{isDe ? 'Geldentnahme' : 'Money withdraw'}</Typography>
            <IconButton onClick={() => setMoneyWithdrawOpen(false)} sx={{ color: C.accent }}><CloseIcon /></IconButton>
          </Box>
          <TextField
            type="number"
            label={isDe ? 'Betrag (€)' : 'Amount (€)'}
            value={withdrawAmount}
            onChange={e => setWithdrawAmount(e.target.value)}
            fullWidth autoFocus InputLabelProps={{ shrink: true }}
            sx={{ ...inputSx, mb: 2 }}
          />
          <TextField
            label={isDe ? 'Grund' : 'Reason'}
            value={withdrawReason}
            onChange={e => setWithdrawReason(e.target.value)}
            placeholder={isDe ? 'z. B. Wechselgeld, Einkauf' : 'e.g. change, supplies'}
            fullWidth InputLabelProps={{ shrink: true }}
            sx={{ ...inputSx, mb: 2 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Button onClick={() => { setWithdrawAmount(''); setWithdrawReason(''); setMoneyWithdrawOpen(false); }} sx={cancelBtn}>{isDe ? 'Abbrechen' : 'Cancel'}</Button>
            <Button onClick={submitMoneyWithdraw} sx={confirmBtn}>{isDe ? 'Erfassen' : 'Record'}</Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Item + add-ons modal — opens when the cashier taps a product card */}
      {productForAddons && (
        <AddItemWithAddOnsModal
          open={addonsModalOpen}
          onClose={() => { setAddonsModalOpen(false); setProductForAddons(null); }}
          product={productForAddons}
          onAddToCart={addToCartFromModal}
          language={'en'}
        />
      )}

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.sev} sx={{ backgroundColor: snack.sev === 'success' ? '#1b5e20' : '#b71c1c', color: '#fff' }}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

// (Google Maps style arrays removed — Saheeh uses Leaflet/OSM)

// ── Shared inline styles ───────────────────────────────────────────
const kbdChip = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  minWidth: 28, height: 22, px: 0.8, ml: 1.2, borderRadius: '6px',
  fontSize: '0.7rem', fontWeight: 700,
  backgroundColor: 'rgba(255,255,255,0.18)', color: '#FFFFFF',
};
const kbdChipDark = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  minWidth: 28, height: 22, px: 0.8, ml: 1.2, borderRadius: '6px',
  fontSize: '0.7rem', fontWeight: 700,
  backgroundColor: 'rgba(26,29,46,0.25)', color: '#1a1d2e',
};
const topBtn = (bg: string) => {
  const isAccent = bg === '#94e815';
  const glowMap: Record<string, string> = {
    '#94e815': '0 3px 12px rgba(148,232,21,0.40)',
    '#F97316': '0 3px 12px rgba(249,115,22,0.40)',
    '#8B5CF6': '0 3px 12px rgba(139,92,246,0.40)',
    '#EF4444': '0 3px 12px rgba(239,68,68,0.40)',
    '#22C55E': '0 3px 12px rgba(34,197,94,0.40)',
    '#6366F1': '0 3px 12px rgba(99,102,241,0.35)',
    '#3B82F6': '0 3px 12px rgba(59,130,246,0.35)',
    '#0891B2': '0 3px 12px rgba(8,145,178,0.35)',
  };
  const gradMap: Record<string, string> = {
    '#94e815': 'linear-gradient(135deg, #7bc912 0%, #94e815 100%)',
    '#F97316': 'linear-gradient(135deg, #EA580C 0%, #F97316 100%)',
    '#8B5CF6': 'linear-gradient(135deg, #7C3AED 0%, #8B5CF6 100%)',
    '#EF4444': 'linear-gradient(135deg, #DC2626 0%, #EF4444 100%)',
    '#22C55E': 'linear-gradient(135deg, #16A34A 0%, #22C55E 100%)',
    '#6366F1': 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
    '#3B82F6': 'linear-gradient(135deg, #1D4ED8 0%, #3B82F6 100%)',
    '#0891B2': 'linear-gradient(135deg, #0E7490 0%, #0891B2 100%)',
  };
  return {
    background: gradMap[bg] || `linear-gradient(135deg, ${bg} 0%, ${bg} 100%)`,
    color: isAccent ? '#1a1d2e' : '#FFFFFF',
    textTransform: 'none' as const, fontWeight: 700, borderRadius: '11px',
    px: 1.8, py: 1.0, fontSize: '0.87rem', whiteSpace: 'nowrap' as const,
    boxShadow: glowMap[bg] || '0 3px 10px rgba(0,0,0,0.25)',
    border: 'none',
    transition: 'all 0.15s ease',
    '&:hover': {
      filter: 'brightness(1.1)',
      transform: 'translateY(-1px)',
      boxShadow: glowMap[bg]?.replace('0.40', '0.55').replace('0.35', '0.50') || '0 5px 16px rgba(0,0,0,0.35)',
      background: gradMap[bg] || bg,
    },
  };
};
// Shared dropdown style for customer/company/street autocomplete pickers.
const suggestionDropdown = {
  position: 'absolute' as const,
  top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20,
  backgroundColor: '#252838',
  border: '1px solid #3a3d52',
  borderRadius: '12px',
  boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
  overflow: 'hidden',
  maxHeight: 320,
  overflowY: 'auto' as const,
};
const suggestionRow = {
  px: 2, py: 1.2, cursor: 'pointer',
  borderBottom: '1px solid #2a2d40',
  '&:hover': { backgroundColor: 'rgba(148,232,21,0.10)' },
  '&:last-child': { borderBottom: 'none' },
};

// Small orange badge that floats above the field label when the field is
// required-but-empty. Positioned at the top-RIGHT so it doesn't overlap the
// upper-left label ("PHONE NUMBER" / "NAME").
const requiredBadge = {
  position: 'absolute' as const,
  top: -8, right: 12, zIndex: 2,
  backgroundColor: '#252838',
  color: '#F97316',
  px: 0.8, py: 0.1,
  fontSize: '0.65rem',
  fontWeight: 800,
  letterSpacing: '0.06em',
  borderRadius: '6px',
  border: '1px solid #F97316',
  pointerEvents: 'none' as const,
};

// Orange-tinted outline merged on top of the regular `inputSx`.
const requiredInputSx = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#F97316 !important', borderWidth: '1.5px' },
    '&:hover fieldset': { borderColor: '#F97316 !important' },
    '&.Mui-focused fieldset': { borderColor: '#F97316 !important' },
  },
  '& .MuiInputLabel-root': { color: '#F97316 !important' },
};

const topActionBtn = (bg: string) => {
  const glowMap: Record<string, string> = {
    '#22C55E': '0 4px 18px rgba(34,197,94,0.45)',
    '#94e815': '0 4px 18px rgba(148,232,21,0.45)',
    '#3B82F6': '0 4px 18px rgba(59,130,246,0.40)',
    '#7C3AED': '0 4px 18px rgba(124,58,237,0.40)',
    '#374151': '0 4px 12px rgba(0,0,0,0.30)',
  };
  const gradMap: Record<string, string> = {
    '#22C55E': 'linear-gradient(135deg, #15803D 0%, #22C55E 100%)',
    '#94e815': 'linear-gradient(135deg, #7bc912 0%, #94e815 100%)',
    '#3B82F6': 'linear-gradient(135deg, #1D4ED8 0%, #3B82F6 100%)',
    '#7C3AED': 'linear-gradient(135deg, #6D28D9 0%, #7C3AED 100%)',
    '#374151': 'linear-gradient(135deg, #1F2937 0%, #374151 100%)',
  };
  return {
    background: gradMap[bg] || `linear-gradient(135deg, ${bg} 0%, ${bg} 100%)`,
    color: bg === '#94e815' ? '#1a1d2e' : '#FFFFFF',
    textTransform: 'none' as const, fontWeight: 700, borderRadius: '14px',
    px: { xs: 1.5, md: 2.5 }, py: { xs: 1.2, md: 1.35 },
    fontSize: { xs: '0.88rem', md: '0.95rem' },
    width: { xs: '100%', md: 'auto' },
    justifyContent: 'center',
    boxShadow: glowMap[bg] || '0 4px 14px rgba(0,0,0,0.25)',
    border: 'none',
    transition: 'all 0.15s ease',
    '&:hover': {
      filter: 'brightness(1.1)',
      transform: 'translateY(-2px)',
      boxShadow: glowMap[bg]?.replace('0.45', '0.65').replace('0.40', '0.58').replace('0.30', '0.45') || '0 6px 20px rgba(0,0,0,0.4)',
      background: gradMap[bg] || bg,
    },
  };
};
const qtyBtn = {
  width: 22, height: 22, backgroundColor: '#3a3d52', color: '#FFFFFF',
  '&:hover': { backgroundColor: '#94e815', color: '#1a1d2e' },
};
const catPill = (active: boolean) => ({
  display: 'flex', alignItems: 'center', gap: 1,
  px: 1.2, py: 0.9, borderRadius: '10px', cursor: 'pointer',
  border: `1.5px solid ${active ? '#94e815' : '#3a3d52'}`,
  backgroundColor: active ? 'rgba(148,232,21,0.18)' : '#1e2139',
  color: active ? '#94e815' : '#FFFFFF',
  fontWeight: active ? 700 : 500,
  transition: 'all 0.15s ease',
  '&:hover': { borderColor: '#94e815', backgroundColor: 'rgba(148,232,21,0.08)' },
});
const modalPaper = {
  borderRadius: '20px',
  backgroundColor: `${'#252838'} !important`,
  border: `1px solid #3a3d52`,
  color: '#FFFFFF',
};
const modalHeader = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5,
};
const confirmBtn = {
  backgroundColor: '#94e815', color: '#1a1d2e', borderRadius: '12px',
  px: 3, py: 1.3, textTransform: 'none' as const, fontWeight: 800,
  '&:hover': { backgroundColor: '#7bc912' },
};
const cancelBtn = {
  backgroundColor: '#374151', color: '#FFFFFF', borderRadius: '12px',
  px: 3, py: 1.3, textTransform: 'none' as const, fontWeight: 700,
  '&:hover': { backgroundColor: '#1F2937' },
};

// ── Recent delivery orders strip (shown under the map on the customer step) ──
// Liefersoft parity: All orders / Preorders / Not paid / Invoices tabs +
// Nr / Time / Platform / Customer / Sum / Order type / Status columns.
const RecentDeliveryOrders: React.FC<{ isDe: boolean }> = ({ isDe }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [tab, setTab] = useState<'all' | 'preorders' | 'not_paid' | 'invoices'>('all');
  const navigate = useNavigate();
  // Action-menu modal (Liefersoft parity): clicking a row opens a small
  // sheet with Show order / Print / Cancel / Change payment / Book on driver
  // / Complete order / Receipt — same options Liefersoft shows.
  const [actionOrder, setActionOrder] = useState<any | null>(null);
  const [bookDriverFor, setBookDriverFor] = useState<any | null>(null);
  const [changePayFor, setChangePayFor] = useState<any | null>(null);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionSnack, setActionSnack] = useState<{ open: boolean; sev: 'success' | 'error'; msg: string }>({ open: false, sev: 'success', msg: '' });

  // Load drivers once (cached for the lifetime of this page).
  useEffect(() => {
    (async () => {
      try {
        const db = getFirestore();
        const snap = await getDocs(collection(db, 'drivers'));
        const list: any[] = [];
        snap.forEach(d => {
          const data = d.data() as any;
          list.push({
            id: d.id,
            email: data.email || d.id,
            name: data.name || data.first_name || data.email || d.id,
            code: data.code || data.driver_code || '',
            is_active: data.is_active !== false,
          });
        });
        setDrivers(list.filter(x => x.is_active));
      } catch { /* non-critical */ }
    })();
  }, []);

  // Centralised order mutation + refresh. Each action mutates the row in
  // local state too so the user sees the change before the snapshot listener
  // catches up.
  const patchOrder = async (orderId: string, patch: any, successMsg: string) => {
    setActionBusy(true);
    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'orders', orderId), { ...patch, updated_at: serverTimestamp() });
      setRows(prev => prev.map(r => r.id === orderId ? { ...r, ...patch } : r));
      setActionSnack({ open: true, sev: 'success', msg: successMsg });
    } catch (e: any) {
      console.error('[ACTION MENU] patch failed', e);
      setActionSnack({ open: true, sev: 'error', msg: e?.message || 'Update failed' });
    } finally {
      setActionBusy(false);
    }
  };

  const reprintReceipt = async (order: any) => {
    setActionBusy(true);
    try {
      const ps = FirestorePrintService.getInstance();
      // Use whatever items we have on the row; if missing, fetch the full doc.
      let items = order.items;
      if (!items) {
        const snap = await getDocs(query(collection(getFirestore(), 'orders'), where('__name__', '==', order.id)));
        snap.forEach(d => { items = (d.data() as any).items || []; });
      }
      await ps.printCashierReceipt(order.id, order.id, {
        orderNumber: order.order_number || order.id,
        customerName: order.customer || 'Walk-in',
        customerAddress: order.address || '',
        customerPhone: order.customer_phone || '',
        orderDate: order.created_at.toLocaleDateString('de-DE'),
        orderTime: order.created_at.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
        items: (items || []).map((it: any, idx: number) => ({ ...it, itemNumber: idx + 1 })),
        subtotal: Math.round((order.total / 1.07) * 100) / 100,
        tax: Math.round((order.total - (order.total / 1.07)) * 100) / 100,
        total: order.total,
        paymentMethod: order.payment_method || 'cash',
        paymentStatus: order.payment_status || 'pending',
        order_type: order.order_type,
      });
      setActionSnack({ open: true, sev: 'success', msg: isDe ? 'Beleg gedruckt' : 'Receipt printed' });
    } catch (e: any) {
      setActionSnack({ open: true, sev: 'error', msg: e?.message || 'Print failed' });
    } finally {
      setActionBusy(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const db = getFirestore();
        const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
        const q = query(
          collection(db, 'orders'),
          where('created_at', '>=', startOfDay),
        );
        const snap = await getDocs(q);
        const myBranchId = currentBranchId();
        const list: any[] = [];
        snap.forEach(d => {
          const data = d.data() as any;
          const orderBranch = (data.branch_id || 'huerth') as string;
          if (orderBranch !== myBranchId) return;
          const ca = data.created_at?.toDate?.() || new Date(data.created_at || Date.now());
          const scheduled = data.scheduled_for?.toDate?.()
            || (data.scheduled_for ? new Date(data.scheduled_for) : null);
          list.push({
            id: d.id,
            // Older docs may have been created without an order_number (the
            // field used to be set as an empty string and never backfilled).
            // Show the last 5 chars of the doc ID prefixed with # so the UI
            // doesn't dump the full 20-char Firestore ID into the Nr column.
            order_number: data.order_number || `#${d.id.slice(-5)}`,
            customer: data.customer_name || 'Walk-in',
            customer_phone: data.customer_phone || '',
            address: data.delivery_address || data.customer_address || '',
            total: Number(data.total || 0),
            items: data.items || [],
            status: data.status || 'pending',
            payment_status: data.payment_status || 'pending',
            payment_method: data.payment_method || 'cash',
            rider_email: data.rider_email || '',
            order_type: data.order_type || 'delivery',
            platform: data.platform || data.source || 'pos',
            is_preorder: !!scheduled && scheduled.getTime() > Date.now(),
            invoice_number: data.invoice_number,
            // Geocoded coords for the map. CheckoutDialog writes these on order
            // creation (2026-03-29 pipeline). Older orders may have null —
            // those simply don't get a map pin.
            delivery_lat: typeof data.delivery_latitude === 'number' ? data.delivery_latitude : null,
            delivery_lng: typeof data.delivery_longitude === 'number' ? data.delivery_longitude : null,
            created_at: ca,
          });
        });
        list.sort((a, b) => b.created_at.getTime() - a.created_at.getTime());
        setRows(list);
      } catch (e) { /* non-critical */ }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (tab === 'preorders') return rows.filter(r => r.is_preorder);
    if (tab === 'not_paid') return rows.filter(r => r.payment_status !== 'paid' && r.payment_status !== 'completed' && r.status !== 'cancelled');
    if (tab === 'invoices') return rows.filter(r => !!r.invoice_number);
    return rows;
  }, [rows, tab]);

  const notPaidCount = useMemo(() => rows.filter(r => r.payment_status !== 'paid' && r.payment_status !== 'completed' && r.status !== 'cancelled').length, [rows]);

  const tabs: Array<{ key: typeof tab; label: string; badge?: number; color: string }> = [
    { key: 'all', label: isDe ? 'Alle Bestellungen' : 'All orders', color: '#3B82F6' },
    { key: 'preorders', label: isDe ? 'Vorbestellungen' : 'Preorders', color: '#3B82F6' },
    { key: 'not_paid', label: isDe ? 'Nicht bezahlt' : 'Not paid', color: '#EF4444', badge: notPaidCount },
    { key: 'invoices', label: isDe ? 'Rechnungen' : 'Invoices', color: '#3B82F6' },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
      {/* Tab bar */}
      <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', borderBottom: '1px solid #3a3d52', pb: 0.8 }}>
        {tabs.map(t => (
          <Box key={t.key} onClick={() => setTab(t.key)} sx={{
            position: 'relative', cursor: 'pointer',
            px: 2, py: 0.9, borderRadius: '10px',
            fontWeight: 700, fontSize: '0.9rem',
            backgroundColor: tab === t.key ? 'rgba(96,165,250,0.18)' : 'transparent',
            color: tab === t.key ? t.color : '#cbd5e1',
            border: `1.5px solid ${tab === t.key ? t.color : 'transparent'}`,
            '&:hover': { color: t.color },
          }}>
            {t.label}
            {!!t.badge && t.badge > 0 && (
              <Box component="span" sx={{
                position: 'absolute', top: -6, right: -6,
                minWidth: 18, height: 18, px: 0.6, borderRadius: '9px',
                backgroundColor: '#EF4444', color: '#FFFFFF',
                fontSize: '0.7rem', fontWeight: 800,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>{t.badge}</Box>
            )}
          </Box>
        ))}
      </Box>

      {/* Order rows — mobile cards / desktop table */}
      <Box sx={{
        backgroundColor: '#252838', border: '1px solid #3a3d52', borderRadius: '12px',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {filtered.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center', color: '#94a3b8', fontSize: '0.9rem' }}>
            {isDe ? 'Keine Bestellungen' : 'No orders'}
          </Box>
        ) : (
          <>
            {/* Desktop table header — hidden on mobile */}
            <Box sx={{
              display: { xs: 'none', md: 'grid' },
              gridTemplateColumns: '60px 70px 120px minmax(0, 1fr) 110px 140px 130px',
              px: 1.5, py: 1, borderBottom: '1px solid #3a3d52',
              color: '#cbd5e1', fontSize: '0.72rem', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.06em',
            }}>
              <Box>Nr</Box>
              <Box>{isDe ? 'Zeit' : 'Time'}</Box>
              <Box>{isDe ? 'Plattform' : 'Platform'}</Box>
              <Box>{isDe ? 'Kunde' : 'Customer'}</Box>
              <Box sx={{ textAlign: 'right' }}>{isDe ? 'Summe' : 'Sum'}</Box>
              <Box>{isDe ? 'Bestellart' : 'Order type'}</Box>
              <Box sx={{ textAlign: 'right' }}>Status</Box>
            </Box>
            {filtered.map((r) => (
              <Box key={r.id} onClick={() => setActionOrder(r)} sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(148,232,21,0.06)' }, borderBottom: '1px solid #2a2d40' }}>
                {/* Mobile card */}
                <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', px: 1.5, py: 1.2, gap: 0.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ color: '#60A5FA', fontWeight: 700, fontSize: '0.95rem' }}>#{r.order_number}</Typography>
                    <Typography sx={{ color: '#94e815', fontWeight: 800, fontSize: '1rem' }}>{r.total.toFixed(2)} €</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '0.88rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.customer}</Typography>
                    <StatusPill status={r.status} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>{r.created_at.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</Typography>
                    <PlatformBadge platform={r.platform} />
                    <OrderTypeBadge orderType={r.order_type} />
                  </Box>
                </Box>
                {/* Desktop row */}
                <Box sx={{
                  display: { xs: 'none', md: 'grid' },
                  gridTemplateColumns: '60px 70px 120px minmax(0, 1fr) 110px 140px 130px',
                  px: 1.5, py: 0.9, fontSize: '0.9rem', alignItems: 'center',
                }}>
                  <Box sx={{ color: '#60A5FA', fontWeight: 700 }}>{r.order_number}</Box>
                  <Box sx={{ color: '#FFFFFF' }}>{r.created_at.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</Box>
                  <Box><PlatformBadge platform={r.platform} /></Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: '#FFFFFF', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.customer}</Typography>
                    {r.address && <Typography sx={{ color: '#94a3b8', fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.address}</Typography>}
                  </Box>
                  <Box sx={{ color: '#94e815', fontWeight: 700, textAlign: 'right' }}>{r.total.toFixed(2)} €</Box>
                  <Box><OrderTypeBadge orderType={r.order_type} /></Box>
                  <Box sx={{ textAlign: 'right' }}><StatusPill status={r.status} /></Box>
                </Box>
              </Box>
            ))}
          </>
        )}
      </Box>

      {/* ─── Order action menu (Liefersoft parity) ────────────────────── */}
      <Dialog open={!!actionOrder} onClose={() => setActionOrder(null)} PaperProps={{ sx: { backgroundColor: '#1e2238', border: '1px solid #2a2d40', borderRadius: '14px', width: { xs: 'calc(100vw - 32px)', sm: 400 }, maxWidth: '100%' } }}>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>
                {isDe ? 'Bestellnummer' : 'Order number'} #{actionOrder?.order_number}
              </Typography>
              <Typography sx={{ color: '#94e815', fontWeight: 700, fontSize: '1rem', mt: 0.4 }}>
                {Number(actionOrder?.total || 0).toFixed(2).replace('.', ',')} €
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ px: 0.8, py: 0.2, borderRadius: '6px', backgroundColor: '#2a2d40', color: '#94a3b8', fontSize: '0.7rem', fontWeight: 700 }}>ESC</Box>
              <IconButton onClick={() => setActionOrder(null)} sx={{ color: '#F59E0B' }}><CloseIcon /></IconButton>
            </Box>
          </Box>
          {actionBusy && <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}><CircularProgress size={20} sx={{ color: '#94e815' }} /></Box>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            {([
              { key: 'show', label: isDe ? 'Bestellung anzeigen' : 'Show order', color: '#22C55E', primary: true },
              { key: 'print', label: isDe ? 'Drucken' : 'Print', color: '#94a3b8' },
              { key: 'cancel', label: isDe ? 'Bestellung stornieren' : 'Cancel order', color: '#EF4444' },
              { key: 'change_payment', label: isDe ? 'Zahlungsmethode ändern' : 'Change payment method', color: '#94a3b8' },
              { key: 'book_driver', label: isDe ? 'Auf Fahrer buchen' : 'Book on driver', color: '#94a3b8' },
              { key: 'complete', label: isDe ? 'Bestellung abschließen' : 'Complete order', color: '#94a3b8' },
              { key: 'receipt', label: isDe ? 'Beleg drucken' : 'Receipt', color: '#94a3b8' },
            ] as const).map(act => {
              const isPrimary = (act as any).primary;
              return (
                <Box
                  key={act.key}
                  onClick={async () => {
                    const o = actionOrder;
                    if (!o) return;
                    switch (act.key) {
                      case 'show':
                        setActionOrder(null);
                        navigate('/orders-legacy');
                        break;
                      case 'print':
                      case 'receipt':
                        await reprintReceipt(o);
                        break;
                      case 'cancel':
                        if (window.confirm(isDe ? 'Wirklich stornieren?' : 'Cancel this order?')) {
                          await patchOrder(o.id, { status: 'cancelled' }, isDe ? 'Storniert' : 'Cancelled');
                          setActionOrder(null);
                        }
                        break;
                      case 'change_payment':
                        setChangePayFor(o);
                        setActionOrder(null);
                        break;
                      case 'book_driver':
                        setBookDriverFor(o);
                        setActionOrder(null);
                        break;
                      case 'complete':
                        await patchOrder(o.id, { status: 'completed', payment_status: 'paid' }, isDe ? 'Abgeschlossen' : 'Completed');
                        setActionOrder(null);
                        break;
                    }
                  }}
                  sx={{
                    cursor: 'pointer', userSelect: 'none',
                    px: 2, py: 1.3, borderRadius: '10px',
                    border: `1.5px solid ${isPrimary ? act.color : '#2a2d40'}`,
                    backgroundColor: isPrimary ? `${act.color}22` : 'transparent',
                    color: isPrimary ? act.color : '#cbd5e1',
                    fontWeight: 700, fontSize: '0.95rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    '&:hover': { backgroundColor: `${act.color}28`, borderColor: act.color, color: act.color },
                  }}
                >
                  {act.label}
                  {isPrimary && <Box component="span" sx={{ color: act.color }}>✓</Box>}
                </Box>
              );
            })}
          </Box>
        </DialogContent>
      </Dialog>

      {/* ─── Book on driver dialog ─────────────────────────────── */}
      <Dialog open={!!bookDriverFor} onClose={() => setBookDriverFor(null)} PaperProps={{ sx: { backgroundColor: '#1e2238', border: '1px solid #2a2d40', borderRadius: '14px', width: { xs: 'calc(100vw - 32px)', sm: 400 }, maxWidth: '100%', maxHeight: '80vh' } }}>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>
              {isDe ? 'Auf Fahrer buchen' : 'Book on driver'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ px: 0.8, py: 0.2, borderRadius: '6px', backgroundColor: '#2a2d40', color: '#94a3b8', fontSize: '0.7rem', fontWeight: 700 }}>ESC</Box>
              <IconButton onClick={() => setBookDriverFor(null)} sx={{ color: '#F59E0B' }}><CloseIcon /></IconButton>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            {drivers.length === 0 ? (
              <Typography sx={{ color: '#94a3b8', textAlign: 'center', py: 3 }}>
                {isDe ? 'Keine aktiven Fahrer' : 'No active drivers'}
              </Typography>
            ) : drivers.map(d => {
              const isActive = bookDriverFor?.rider_email === d.email;
              return (
                <Box
                  key={d.id}
                  onClick={async () => {
                    if (!bookDriverFor) return;
                    await patchOrder(bookDriverFor.id, {
                      rider_email: d.email,
                      driver_name: d.name,
                      status: 'assigned',
                    }, isDe ? `Zugewiesen an ${d.name}` : `Assigned to ${d.name}`);
                    setBookDriverFor(null);
                  }}
                  sx={{
                    cursor: 'pointer', userSelect: 'none',
                    px: 2, py: 1.3, borderRadius: '10px',
                    border: `1.5px solid ${isActive ? '#22C55E' : '#2a2d40'}`,
                    backgroundColor: isActive ? '#22C55E22' : 'transparent',
                    color: isActive ? '#22C55E' : '#cbd5e1',
                    fontWeight: 700, fontSize: '0.95rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    '&:hover': { backgroundColor: '#22C55E20', borderColor: '#22C55E', color: '#22C55E' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: '#2a2d40', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', color: '#cbd5e1' }}>
                      {d.code || (d.name || '?').slice(0, 2).toUpperCase()}
                    </Box>
                    {d.name}
                  </Box>
                  {isActive && <Box component="span">✓</Box>}
                </Box>
              );
            })}
          </Box>
        </DialogContent>
      </Dialog>

      {/* ─── Change payment method dialog ─────────────────────────────── */}
      <Dialog open={!!changePayFor} onClose={() => setChangePayFor(null)} PaperProps={{ sx: { backgroundColor: '#1e2238', border: '1px solid #2a2d40', borderRadius: '14px', width: { xs: 'calc(100vw - 32px)', sm: 400 }, maxWidth: '100%' } }}>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>
              {isDe ? 'Zahlungsmethode ändern' : 'Change payment method'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ px: 0.8, py: 0.2, borderRadius: '6px', backgroundColor: '#2a2d40', color: '#94a3b8', fontSize: '0.7rem', fontWeight: 700 }}>ESC</Box>
              <IconButton onClick={() => setChangePayFor(null)} sx={{ color: '#F59E0B' }}><CloseIcon /></IconButton>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            {([
              { key: 'cash', label: isDe ? 'Barzahlung' : 'Cash', color: '#22C55E' },
              { key: 'card', label: isDe ? 'Karte' : 'Card', color: '#3B82F6' },
              { key: 'paypal', label: 'PayPal', color: '#3B82F6' },
              { key: 'invoice', label: isDe ? 'Rechnung' : 'Invoice', color: '#A855F7' },
            ] as const).map(opt => {
              const isActive = changePayFor?.payment_method === opt.key;
              return (
                <Box
                  key={opt.key}
                  onClick={async () => {
                    if (!changePayFor) return;
                    await patchOrder(changePayFor.id, { payment_method: opt.key }, isDe ? 'Zahlungsmethode aktualisiert' : 'Payment method updated');
                    setChangePayFor(null);
                  }}
                  sx={{
                    cursor: 'pointer', userSelect: 'none',
                    px: 2, py: 1.2, borderRadius: '10px',
                    border: `1.5px solid ${isActive ? opt.color : `${opt.color}55`}`,
                    backgroundColor: isActive ? `${opt.color}28` : `${opt.color}15`,
                    color: opt.color, fontWeight: 700, fontSize: '0.95rem',
                    '&:hover': { backgroundColor: `${opt.color}28`, borderColor: opt.color },
                  }}
                >
                  {opt.label}
                </Box>
              );
            })}
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar open={actionSnack.open} autoHideDuration={3000} onClose={() => setActionSnack(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={actionSnack.sev} sx={{ backgroundColor: actionSnack.sev === 'success' ? '#1b5e20' : '#b71c1c', color: '#fff' }}>{actionSnack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

const PlatformBadge: React.FC<{ platform: string }> = ({ platform }) => {
  const p = (platform || '').toLowerCase();
  const map: Record<string, { label: string; color: string }> = {
    lieferando: { label: 'Lieferando', color: '#F97316' },
    ubereats: { label: 'Uber Eats', color: '#22C55E' },
    'uber-eats': { label: 'Uber Eats', color: '#22C55E' },
    wolt: { label: 'Wolt', color: '#06B6D4' },
    qr: { label: 'QR', color: '#94e815' },
    qr_order: { label: 'QR', color: '#94e815' },
    webshop: { label: 'Webshop', color: '#A855F7' },
    pos: { label: 'POS', color: '#3B82F6' },
  };
  const cfg = map[p] || { label: platform || 'POS', color: '#94a3b8' };
  return (
    <Box component="span" sx={{
      display: 'inline-block', px: 0.9, py: 0.25, borderRadius: '6px',
      fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.02em',
      backgroundColor: `${cfg.color}22`, color: cfg.color,
      border: `1px solid ${cfg.color}55`,
    }}>{cfg.label}</Box>
  );
};

const OrderTypeBadge: React.FC<{ orderType: string }> = ({ orderType }) => {
  const t = (orderType || '').toLowerCase();
  const map: Record<string, { label: string; color: string }> = {
    delivery: { label: '🛵 Delivery', color: '#94e815' },
    pickup: { label: '🛍️ Pickup', color: '#3B82F6' },
    takeaway: { label: '🛍️ Takeaway', color: '#3B82F6' },
    dine_in: { label: '🍽️ Dine-in', color: '#F59E0B' },
    restaurant: { label: '🍽️ Dine-in', color: '#F59E0B' },
    counter: { label: '🏪 Counter', color: '#A855F7' },
  };
  const cfg = map[t] || { label: orderType || '—', color: '#94a3b8' };
  return (
    <Box component="span" sx={{
      display: 'inline-block', px: 0.8, py: 0.25, borderRadius: '6px',
      fontSize: '0.72rem', fontWeight: 700,
      color: cfg.color, backgroundColor: `${cfg.color}1f`,
    }}>{cfg.label}</Box>
  );
};

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const s = (status || '').toLowerCase();
  const map: Record<string, { label: string; color: string }> = {
    pending: { label: 'Pending', color: '#94a3b8' },
    confirmed: { label: 'Confirmed', color: '#F59E0B' },
    preparing: { label: 'Preparing', color: '#F97316' },
    ready: { label: 'Ready', color: '#3B82F6' },
    assigned: { label: 'Assigned', color: '#F59E0B' },
    out_for_delivery: { label: 'On the way', color: '#8B5CF6' },
    delivered: { label: 'Delivered', color: '#22C55E' },
    completed: { label: 'Completed', color: '#22C55E' },
    cancelled: { label: 'Cancelled', color: '#EF4444' },
  };
  const cfg = map[s] || { label: status, color: '#94a3b8' };
  return (
    <Box component="span" sx={{
      display: 'inline-block', px: 1, py: 0.3, borderRadius: '999px',
      fontSize: '0.72rem', fontWeight: 700,
      color: cfg.color, backgroundColor: `${cfg.color}26`,
      border: `1px solid ${cfg.color}55`,
    }}>{cfg.label}</Box>
  );
};

export default DeliveryPOSPage;
